import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useCreateGrn, useGrn, useGrnItems, useAddGrnItem, useUpdateGrnItem, useConfirmGrn, useDeleteGrnItem } from '../hooks/useGrn';
import type { CreateGrnItemPayload } from '../services/grnService';
import { useClient } from '../hooks/useClients';
import { useZones } from '../hooks/useWarehouse';
import { useUser } from '../hooks/useUsers';
import { gateEntryService } from '../services/gateEntryService';
import { warehouseService } from '../services/warehouseService';
import { clientService } from '../services/clientService';
import { grnService } from '../services/grnService';
import { useDebounce } from '../hooks/useDebounce';
import { uomService, Uom } from '../services/uomService';
import { isPerPalletClient } from '../utils/clientTypeHelpers';
import { useConfirm } from '../hooks/useConfirm';
import { useToast } from '../contexts/ToastContext';
import type { ClientSpaceInfo } from '../types';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';

// LR Date defaults to today but stays editable.
const todayStr = () => new Date().toISOString().slice(0, 10);

// Temporarily hidden per request — flip to true to bring back the
// select-products-for-shared-pallet checkboxes in the Products table.
const SHOW_PALLET_SELECTION_CHECKBOXES = false;

interface PanelSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Themed dropdown for the pallet-assignment panel, matching ResponsiveSelect's
 * option-list design. The list renders position:fixed through a portal so the
 * panel's scrollable placement tables and the mobile bottom sheet cannot clip it.
 */
function PanelSelect({ options, value, onChange, placeholder, disabled = false, className = '' }: {
  options: PanelSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);
  const selected = options.find((o) => o.value !== '' && o.value === value);

  const toggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const openUp = window.innerHeight - r.bottom < 270 && r.top > 270;
      setPos({
        top: openUp ? undefined : r.bottom + 6,
        bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
        left: r.left,
        width: Math.max(r.width, 160),
      });
    }
    setOpen((v) => !v);
  };

  // The list is viewport-anchored, so outside scroll/resize invalidates its
  // position — but scrolling WITHIN the option list must not close it.
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        disabled={disabled}
        onClick={toggle}
        className={`text-left w-full px-2.5 py-2 min-h-[40px] border border-gray-200 rounded-lg bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors ${open ? 'ring-2 ring-indigo-500 border-indigo-500' : ''} ${className}`}
      >
        <span className={`block truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[70] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
            style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width }}
          >
            <div ref={listRef} className="max-h-60 overflow-y-auto overscroll-contain py-1">
              {options.map((o) => {
                const isSel = o.value !== '' && o.value === value;
                return (
                  <button
                    key={o.value || '__empty'}
                    type="button"
                    disabled={o.disabled}
                    onClick={() => { if (o.disabled) return; onChange(o.value); setOpen(false); }}
                    className={`w-full text-left px-3.5 py-2.5 min-h-[44px] lg:min-h-0 lg:py-2 flex items-center justify-between text-sm transition-colors ${
                      o.disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isSel
                        ? 'bg-brand-50 text-brand-700 font-semibold'
                        : o.value === ''
                        ? 'text-gray-500 hover:bg-gray-50 active:bg-gray-50'
                        : 'text-gray-800 hover:bg-gray-50 active:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {isSel && (
                      <svg className="w-4 h-4 text-brand-600 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

// Quantity fields are plain digit strings — strip anything non-numeric and
// collapse leading zeros (typing into a populated "2" without clearing it
// first would otherwise leave a stray "02") while still allowing a bare "0".
const sanitizeIntegerQty = (value: string): string => {
  const digitsOnly = value.replace(/[^0-9]/g, '');
  if (!digitsOnly) return '';
  return digitsOnly.replace(/^0+(?=\d)/, '');
};

// Same idea as sanitizeIntegerQty but keeps a single decimal point, for
// sqft/weight-style fields (pallet size, weight per unit).
const sanitizeDecimalQty = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

// Helper to format datetime for datetime-local input (in local timezone)
const formatDateTimeLocal = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Build a human-readable location from enhanced storageDetails
// (ground → zone code, rack → "RACK / LOCATION"). Used to show where a
// shared pallet physically sits so the product can be located.
const formatStorageLocations = (storageDetails: any): string => {
  if (!Array.isArray(storageDetails)) return '';
  return storageDetails
    .map((s: any) => {
      if (!s || typeof s !== 'object') return '';
      if (s.rackCode || s.locationCode) {
        return s.rackCode && s.locationCode ? `${s.rackCode} / ${s.locationCode}` : (s.locationCode || s.rackCode);
      }
      // Prefer the specific zone-location slot code (e.g. "G8-03") when the
      // pallet was assigned to one; otherwise fall back to the bare zone code.
      return s.zoneLocationCode || s.zoneCode || '';
    })
    .filter(Boolean)
    .join(', ');
};

// Label a rack/zone-location option with its remaining free space, mirroring
// the "X% free · Y sq ft" style already used for rack dropdowns, so the user
// can judge available space before assigning a pallet to a specific location.
// A location already holding another client/product's pallet — full OR
// partial, rack or LBD — surfaces who and since when, rather than reading as
// silently available just because it still has free sqft.
const formatLocationSpaceLabel = (loc: any): string => {
  if (!loc) return '';
  if (loc.isHeld) return ' 🔒 (Reserved by another item)';
  const availPct = loc.occupiedPercentage != null ? Math.max(0, Math.round(100 - Number(loc.occupiedPercentage))) : 100;
  if (loc.occupiedBy) {
    const since = loc.occupiedBy.occupiedSince
      ? ` since ${new Date(loc.occupiedBy.occupiedSince).toLocaleDateString()}`
      : '';
    const who = `Occupied by ${loc.occupiedBy.clientName} — ${loc.occupiedBy.productDescription}${since}`;
    return loc.status === 'occupied' ? ` 🔒 (${who})` : ` — ${availPct}% free (${who})`;
  }
  if (loc.status === 'occupied') return ' 🔒 (Occupied)';
  const sqFtStr = loc.availableSqFt != null ? ` · ${loc.availableSqFt} sq ft avail.` : '';
  return ` — ${availPct}% free${sqFtStr}`;
};

// First-come-first-serve auto-assignment shared by Ground, Rack, and LBD
// placement — `containers` (zones or racks) must already be in the intended
// try-order (alphabetical by code). Fills each row with the first location,
// scanning containers in order, that has enough free % for that row. The
// LAST row, when the quantity is a genuine partial pallet, prefers reusing
// an already-partial slot with just enough room over a fully-empty one, so
// a small leftover doesn't burn a whole empty slot. Returns null for a row
// when nothing available anywhere satisfies it — the caller leaves that row
// for the user to fill in manually.
function autoAssignFirstFit(
  containers: Array<{ id: number; locations: any[] }>,
  rowCount: number,
  qtyPerPallet: number | null | undefined,
  totalQty: number,
  alreadyUsedLocationIds: Set<number>,
): Array<{ containerId: number; locationId: number } | null> {
  const TOLERANCE = 0.5;
  const remainder = qtyPerPallet && qtyPerPallet > 0 ? totalQty % qtyPerPallet : 0;
  const used = new Set(alreadyUsedLocationIds);
  const results: Array<{ containerId: number; locationId: number } | null> = [];

  for (let i = 0; i < rowCount; i++) {
    const isLast = i === rowCount - 1;
    const requiredFreePct = isLast && remainder > 0 ? (remainder / (qtyPerPallet as number)) * 100 : 100;
    let picked: { containerId: number; locationId: number } | null = null;

    if (requiredFreePct < 100) {
      let bestPartial: { containerId: number; locationId: number; freePct: number } | null = null;
      for (const container of containers) {
        for (const loc of container.locations) {
          if (!loc?.locationId || used.has(loc.locationId) || loc.isHeld) continue;
          const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
          if (freePct >= 100 - TOLERANCE) continue; // fully empty — reserved for the fallback pass below
          if (freePct < requiredFreePct - TOLERANCE) continue;
          if (!bestPartial || freePct < bestPartial.freePct) {
            bestPartial = { containerId: container.id, locationId: loc.locationId, freePct };
          }
        }
      }
      if (bestPartial) picked = { containerId: bestPartial.containerId, locationId: bestPartial.locationId };
    }

    if (!picked) {
      outer: for (const container of containers) {
        for (const loc of container.locations) {
          if (!loc?.locationId || used.has(loc.locationId) || loc.isHeld) continue;
          const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
          if (freePct < requiredFreePct - TOLERANCE) continue;
          picked = { containerId: container.id, locationId: loc.locationId };
          break outer;
        }
      }
    }

    if (picked) used.add(picked.locationId);
    results.push(picked);
  }

  return results;
}

// An LBD (damaged-goods) zone is identified by a zone type of 'lbd' or a code
// starting with 'LBD'. Damaged / c-damaged stock may only be stored in these.
const isLbdZone = (zone: any): boolean => {
  const type = String(zone?.zoneType || '').toLowerCase();
  const code = String(zone?.code || '').toLowerCase();
  return type === 'lbd' || code.startsWith('lbd');
};

// A Heavy zone is identified by a zone type of 'heavy' or a code starting with
// 'HEAVY'. Oversized / heavy pallets may only be stored in these.
const isHeavyZone = (zone: any): boolean => {
  const type = String(zone?.zoneType || '').toLowerCase();
  const code = String(zone?.code || '').toLowerCase();
  return type === 'heavy' || code.startsWith('heavy');
};

// Same LBD identification as zones, but for racks — an LBD rack is for
// damaged / c-damaged stock only and must never appear in the main
// good-stock rack picker.
const isLbdRack = (rack: any): boolean => {
  const type = String(rack?.rackType || '').toLowerCase();
  const code = String(rack?.code || '').toLowerCase();
  return type === 'lbd' || code.startsWith('lbd');
};

const zoneTypeLabel = (zone: any): string => {
  const type = String(zone?.zoneType || '').toLowerCase();
  if (type === 'heavy') return 'Heavy';
  if (type === 'lbd') return 'LBD';
  return 'Regular';
};

const rackTypeLabel = (rack: any): string => {
  const type = String(rack?.rackType || '').toLowerCase();
  if (type === 'heavy') return 'Heavy';
  if (type === 'lbd') return 'LBD';
  return 'Regular';
};

// Dropdown option label: code + its fixed type — no name, no free%/sqft.
const zoneOptionLabel = (zone: any): string => `${zone?.code ?? zone?.name ?? 'Unknown'} — ${zoneTypeLabel(zone)}`;
const rackOptionLabel = (rack: any): string => `${rack?.code ?? rack?.name ?? 'Unknown'} — ${rackTypeLabel(rack)}`;

interface ItemFormData {
  productCategoryId: number | null;
  productTypeId: number | null;
  productMasterId: number | null;
  productDescription: string;
  itemCode: string;
  unitType: string;
  weightPerItem: string;
  batchNo: string;
  skuCode: string;
  customerSapCode: string;
  mfgDate: string;
  expiryDate: string;
  expectedQty: string;
  goodQty: string;
  cdamQty: string;
  damQty: string;
  palletCount: string;
  requiredPalletCount: string;
  remarks: string;
  grossWeightPerItem: string;
  lotNo: string;
  handlingType: 'manual' | 'mhe' | '';
}

type StorageType = 'ground' | 'rack';

interface GroundPlacementFormRow {
  zoneId: number;
  pallets: string;
  zoneLocationId?: number | null;
}

interface RackPlacementFormRow {
  rackId: number | null;
  locationId: number | null;
}

const initialFormData: ItemFormData = {
  productCategoryId: null,
  productTypeId: null,
  productMasterId: null,
  productDescription: '',
  itemCode: '',
  unitType: '',
  weightPerItem: '',
  batchNo: '',
  skuCode: '',
  customerSapCode: '',
  mfgDate: '',
  expiryDate: '',
  expectedQty: '',
  goodQty: '',
  cdamQty: '0',
  damQty: '0',
  palletCount: '',
  requiredPalletCount: '',
  remarks: '',
  grossWeightPerItem: '',
  lotNo: '',
  handlingType: '',
};

export default function GrnCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gateEntryId: urlGateEntryId } = useParams<{ gateEntryId: string }>();
  const { confirm, ConfirmDialog } = useConfirm();
  const toast = useToast();
  
  // Get gateEntryId from URL param or navigation state
  const gateEntryId = urlGateEntryId ? parseInt(urlGateEntryId) : location.state?.gateEntryId || null;

  // Editing an approved (past) GRN: Inward reopened it and navigated here with
  // this flag so the form is relabelled "Edit Past GRN".
  const isPastEdit = Boolean(location.state?.pastEdit);

  const [grnId, setGrnId] = useState<number | null>(null);
  const [gateEntry, setGateEntry] = useState<any>(null);
  const [formData, setFormData] = useState<ItemFormData>(initialFormData);
  const [storageType, setStorageType] = useState<StorageType>('ground');
  const [selectedZoneId, setSelectedZoneId] = useState<number | ''>('');
  const [groundPlacements, setGroundPlacements] = useState<
    GroundPlacementFormRow[]
  >([]);
  const [rackPlacements, setRackPlacements] = useState<RackPlacementFormRow[]>(
    [],
  );
  const [racks, setRacks] = useState<any[]>([]);
  const [isLoadingRacks, setIsLoadingRacks] = useState(false);
  const [locationsByRackId, setLocationsByRackId] = useState<
    Record<number, any[]>
  >({});
  const [loadingLocationsByRackId, setLoadingLocationsByRackId] = useState<
    Record<number, boolean>
  >({});
  const [zoneLocationsByZoneId, setZoneLocationsByZoneId] = useState<
    Record<number, any[]>
  >({});
  const [loadingZoneLocationsByZoneId, setLoadingZoneLocationsByZoneId] = useState<
    Record<number, boolean>
  >({});
  const [zoneOccupancyMap, setZoneOccupancyMap] = useState<
    Record<number, { totalPalletsStored: number; totalLocations: number; availableCount: number }>
  >({});
  const [rackSummaryMap, setRackSummaryMap] = useState<
    Record<number, { totalLocations: number; occupiedCount: number; availableCount: number; locationSizeSqft: number | null; availableSqFt: number | null }>
  >({});
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [clientLocation, setClientLocation] = useState<string>('');
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [supplierSuggestions, setSupplierSuggestions] = useState<string[]>([]);
  const debouncedClientLocation = useDebounce(clientLocation, 300);

  // Autocomplete for the Supplier Name box — suggests previously-used
  // supplier name / address values as the operator types.
  useEffect(() => {
    if (!showSupplierSuggestions) return;
    let cancelled = false;
    grnService.getSupplierLocationSuggestions(debouncedClientLocation.trim() || undefined)
      .then((suggestions) => {
        if (!cancelled) setSupplierSuggestions(suggestions);
      })
      .catch(() => {
        if (!cancelled) setSupplierSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedClientLocation, showSupplierSuggestions]);
  const [grnRemarks, setGrnRemarks] = useState<string>('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [lrNumber, setLrNumber] = useState('');
  const [lrNumberError, setLrNumberError] = useState<string | null>(null);
  const [lrDate, setLrDate] = useState(todayStr());
  const [clientSpaceInfo, setClientSpaceInfo] = useState<ClientSpaceInfo | null>(null);
  const [isLoadingSpaceInfo, setIsLoadingSpaceInfo] = useState(false);
  const [spaceInfoError, setSpaceInfoError] = useState<string | null>(null);
  const [sharedPalletGroups, setSharedPalletGroups] = useState<Array<{
    id: string;
    placementType: 'zone' | 'rack';
    zoneId: number | '';
    rackId: number | null;
    locationId: number | null;
    itemIds: number[];
  }>>([]);
  const [selectedForPalletIds, setSelectedForPalletIds] = useState<number[]>([]);
  const [duplicatingFromItem, setDuplicatingFromItem] = useState<any>(null);
  const [, setPalletMode] = useState<'own' | 'shared'>('own');
  const [assigningPalletForItemId, setAssigningPalletForItemId] = useState<number | null>(null);
  const [cardPalletMode, setCardPalletMode] = useState<'own' | 'shared'>('own');
  const [cardStorageType, setCardStorageType] = useState<'ground' | 'rack'>('ground');
  const [cardPalletCount, setCardPalletCount] = useState<string>('');
  const [cardGroundPlacements, setCardGroundPlacements] = useState<Array<{ zoneId: number | ''; pallets: string; zoneLocationId?: number | null }>>([]);
  const [cardRackPlacements, setCardRackPlacements] = useState<Array<{ rackId: number | null; locationId: number | null }>>([]);
  const [sharedAssignStorageType, setSharedAssignStorageType] = useState<'ground' | 'rack'>('ground');
  const [sharedAssignZoneId, setSharedAssignZoneId] = useState<number | ''>('');
  const [sharedAssignRackId, setSharedAssignRackId] = useState<number | null>(null);
  const [sharedAssignLocationId, setSharedAssignLocationId] = useState<number | null>(null);
  // LBD (damaged-goods) storage for the per-product pallet assignment panel.
  const [cardLbdMode, setCardLbdMode] = useState<'dedicated' | 'shared'>('dedicated');
  const [cardLbdZoneId, setCardLbdZoneId] = useState<number | ''>(''); // shared mode only — dedicated mode uses cardLbdPlacements
  // Dedicated mode targets either an LBD zone or an LBD rack for the whole section, never mixed.
  const [cardLbdPlacementType, setCardLbdPlacementType] = useState<'zone' | 'rack'>('zone');
  const [cardLbdPlacements, setCardLbdPlacements] = useState<Array<{ zoneId: number | ''; pallets: string; zoneLocationId: number | null; rackId?: number | null; locationId?: number | null }>>([]);
  const [cardLbdRemark, setCardLbdRemark] = useState<string>('');
  const [cardLbdSharedPalletRef, setCardLbdSharedPalletRef] = useState<string>('');
  // Oversized / heavy pallet — stored in Heavy zones only. Sized the same as a
  // regular pallet (25 sqft); no per-item custom size is collected.
  const [cardIsOversized, setCardIsOversized] = useState<boolean>(false);
  const itemFormRef = useRef<HTMLDivElement>(null);
  const productSearchRef = useRef<HTMLInputElement>(null);

  // Product master search state
  const [productMasterList, setProductMasterList] = useState<any[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [batchList, setBatchList] = useState<any[]>([]);
  const [uomList, setUomList] = useState<Uom[]>([]);

  const getErrorMessage = (error: any, fallback: string) => {
    if (error?.errors && Array.isArray(error.errors)) {
      return error.errors[0];
    }
    return error?.message || fallback;
  };

  // Check if GRN exists for this gate entry
  const createGrnMutation = useCreateGrn();
  const { data: grnData } = useGrn(grnId);
  
  const {
    data: grnItemsData,
  } = useGrnItems(grnId);
  const addItemMutation = useAddGrnItem();
  const updateItemMutation = useUpdateGrnItem();
  const confirmGrnMutation = useConfirmGrn();
  const deleteItemMutation = useDeleteGrnItem();

  // Get client data for product categories
  const clientId = gateEntry?.clientId || grnData?.data?.clientId;
  const {
    data: clientData,
    isError: clientError,
    error: clientErrorData,
  } = useClient(clientId?.toString());

  // Get approver details if GRN is approved
  const approvedBy = grnData?.data?.approvedBy || grnData?.approvedBy;
  const { data: approverData } = useUser(approvedBy);

  const warehouseId = gateEntry?.warehouseId || grnData?.data?.warehouseId || null;
  const {
    data: zonesData,
    isError: zonesError,
    error: zonesErrorData,
  } = useZones(warehouseId);

  const zones = useMemo(() => {
    // API returns: { success, statusCode, message, data: { items: [...], meta: {...} }, timestamp }
    // So we need to access zonesData.data.items
    if (!zonesData) return [];
    if (zonesData.data && Array.isArray(zonesData.data.items)) {
      return zonesData.data.items;
    }
    // Fallback: check if items is directly on zonesData
    if (Array.isArray(zonesData.items)) {
      return zonesData.items;
    }
    return [];
  }, [zonesData]);

  // Good stock goes to normal zones; damaged / c-damaged stock to LBD zones only.
  const lbdZones = useMemo(() => zones.filter(isLbdZone), [zones]);
  const normalZones = useMemo(
    () => zones.filter((z: any) => !isLbdZone(z)),
    [zones],
  );
  const heavyZones = useMemo(() => zones.filter(isHeavyZone), [zones]);

  // Good-stock racks exclude LBD-type racks — those are for damaged stock
  // only and must never appear in the main pallet-assignment rack pickers.
  const nonLbdRacks = useMemo(() => racks.filter((r: any) => !isLbdRack(r)), [racks]);
  const lbdRacks = useMemo(() => racks.filter((r: any) => isLbdRack(r)), [racks]);

  // Alphabetical-by-code try-order for auto-assignment — "first come first
  // serve" fills the earliest-coded zone/rack's locations before moving on.
  const sortByCode = (list: any[]) => [...list].sort((a, b) => String(a?.code ?? '').localeCompare(String(b?.code ?? '')));
  const normalZonesSorted = useMemo(() => sortByCode(normalZones), [normalZones]);
  const heavyZonesSorted = useMemo(() => sortByCode(heavyZones), [heavyZones]);
  const lbdZonesSorted = useMemo(() => sortByCode(lbdZones), [lbdZones]);
  const racksSorted = useMemo(() => sortByCode(nonLbdRacks), [nonLbdRacks]);

  // Total remaining pallet capacity across the warehouse for the storage type
  // currently selected on the Assign Pallets card — used to cap a manually
  // typed Pallet Count (no requiredPalletCount fetched from the batch) so it
  // can't ask for more pallets than the warehouse can actually hold. Location
  // based, same as racks: counts actual generated zone_locations slots that
  // are still free, not an areaSqft/locationSizeSqft estimate.
  const warehouseZoneCapacity = useMemo(() => {
    const relevantZones = cardIsOversized ? heavyZones : normalZones;
    return relevantZones.reduce((sum: number, z: any) => sum + (zoneOccupancyMap[z.id]?.availableCount || 0), 0);
  }, [cardIsOversized, heavyZones, normalZones, zoneOccupancyMap]);

  const warehouseRackCapacity = useMemo(() => {
    return nonLbdRacks.reduce((sum: number, r: any) => sum + (rackSummaryMap[r.id]?.availableCount || 0), 0);
  }, [nonLbdRacks, rackSummaryMap]);

  // Resolve the human-readable label of an item's assigned LBD zone/rack for display.
  const getLbdZoneLabel = (item: any): string => {
    const details = Array.isArray(item.lbdStorageDetails)
      ? item.lbdStorageDetails
      : [];
    const first = details[0];
    if (!first) return '';
    if (first.rackId != null) {
      const rack = racks.find((r: any) => r.id === first.rackId);
      return rack ? rack.name || rack.code : '';
    }
    const zone = zones.find((z: any) => z.id === first.zoneId);
    return zone ? zone.name || zone.code : '';
  };

  // Initialize GRN on page load - IDEMPOTENT
  useEffect(() => {
    // Guard: Only run once
    if (hasInitialized) return;
    if (!gateEntryId) {
      setIsInitializing(false);
      return;
    }
    
    const initializeGrn = async () => {
      try {
        // Fetch gate entry details for additional info
        const gateEntryResponse = await gateEntryService.getGateEntryById(gateEntryId.toString());
        const ge = gateEntryResponse.data;
        setGateEntry(ge);

        // Pre-fill transport fields from gate entry
        if (ge?.vehicleNumber) setVehicleNo(ge.vehicleNumber);
        if (ge?.driverName)    setDriverName(ge.driverName);
        if (ge?.driverPhone)   setDriverPhone(ge.driverPhone);

        const grnResponse = await createGrnMutation.mutateAsync({ 
          gateEntryId, 
          clientLocation: clientLocation || undefined 
        });
        
        if (grnResponse?.data?.id) {
          setGrnId(grnResponse.data.id);
        } else if (grnResponse?.id) {
          setGrnId(grnResponse.id);
        } else {
          throw new Error('Invalid response from GRN creation');
        }
      } catch (error: any) {
        const message = getErrorMessage(error, 'Failed to initialize GRN');
        toast.error(message);
        navigate('/inward');
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
      }
    };

    initializeGrn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount


  // GRN items
  const items = useMemo(() => {
    return grnItemsData || [];
  }, [grnItemsData]);

  // The item whose "Assign Pallets" panel is currently open — auto-assignment
  // effects below need its qty fields but live at the top level (hooks can't
  // be called inside the items.map() render loop).
  const currentAssigningItem = useMemo(
    () => items.find((i: any) => i.id === assigningPalletForItemId) ?? null,
    [items, assigningPalletForItemId],
  );

  // Calculate totals
  const actualQty = useMemo(() => {
    const good = parseInt(formData.goodQty) || 0;
    const cdam = parseInt(formData.cdamQty) || 0;
    const dam = parseInt(formData.damQty) || 0;
    return good + cdam + dam;
  }, [formData.goodQty, formData.cdamQty, formData.damQty]);

  // Pallet requirements are planned off Good Qty only — CDAM/DAM stock is
  // placed onto pallets manually, outside this auto-calculation.
  const goodQtyNumber = useMemo(() => parseInt(formData.goodQty) || 0, [formData.goodQty]);

  const excessQty = useMemo(() => {
    const expected = parseInt(formData.expectedQty) || 0;
    return actualQty - expected;
  }, [actualQty, formData.expectedQty]);

  const excessStatus = useMemo(() => {
    if (excessQty > 0) return { label: 'Excess', color: 'text-orange-600 bg-orange-100' };
    if (excessQty < 0) return { label: 'Short', color: 'text-red-600 bg-red-100' };
    return { label: 'Matched', color: 'text-green-600 bg-green-100' };
  }, [excessQty]);

  const totalWeight = useMemo(() => {
    const weight = parseFloat(formData.weightPerItem) || 0;
    return (actualQty * weight).toFixed(2);
  }, [actualQty, formData.weightPerItem]);

  const grossTotalWeight = useMemo(() => {
    const gw = parseFloat(formData.grossWeightPerItem) || 0;
    if (!gw) return '';
    return (actualQty * gw).toFixed(2);
  }, [actualQty, formData.grossWeightPerItem]);

  const shortExcess = useMemo(() => {
    return excessQty;
  }, [excessQty]);

  const palletCountNumber = useMemo(() => {
    return parseInt(formData.palletCount) || 0;
  }, [formData.palletCount]);

  const selectedBatchQtyPerPallet = useMemo(() => {
    if (!formData.batchNo) return null;
    const batch = batchList.find((b: any) => b.batchNo === formData.batchNo);
    return batch?.qtyPerPallet ?? null;
  }, [formData.batchNo, batchList]);

  // Drives the SKU Code / Customer SAP Code / Lot No lock below — each field
  // is only read-only when the selected batch actually carries a value for
  // it; a batch missing that particular field still leaves it editable.
  const selectedBatch = useMemo(() => {
    if (!formData.batchNo) return null;
    return batchList.find((b: any) => b.batchNo === formData.batchNo) ?? null;
  }, [formData.batchNo, batchList]);

  // Required Pallets is fully auto-calculated from good qty ÷ qty-per-pallet
  // — not manually editable, for add or edit. CDAM/DAM qty is excluded: those
  // pallets are assigned manually, not planned by this calculation.
  const computedRequiredPalletCount = useMemo(() => {
    return selectedBatchQtyPerPallet && goodQtyNumber > 0
      ? Math.ceil(goodQtyNumber / selectedBatchQtyPerPallet)
      : null;
  }, [selectedBatchQtyPerPallet, goodQtyNumber]);

  useEffect(() => {
    const next = computedRequiredPalletCount ? String(computedRequiredPalletCount) : '';
    setFormData((f) => (f.requiredPalletCount === next ? f : { ...f, requiredPalletCount: next }));
  }, [computedRequiredPalletCount]);

  useEffect(() => {
    if (storageType !== 'rack') return;
    if (palletCountNumber <= 0) {
      setRackPlacements([]);
      return;
    }

    setRackPlacements((prev) => {
      const next = prev.slice(0, palletCountNumber);
      while (next.length < palletCountNumber) {
        next.push({ rackId: null, locationId: null });
      }
      return next;
    });
  }, [palletCountNumber, storageType]);

  useEffect(() => {
    if (!warehouseId) {
      setRacks([]);
      return;
    }

    let cancelled = false;

    const loadRacks = async () => {
      setIsLoadingRacks(true);
      try {
        const response = await warehouseService.getRacks(warehouseId, { limit: 100, status: 'active' });
        if (cancelled) return;
        setRacks(response?.items || []);
      } catch (error) {
        if (!cancelled) {
          setRacks([]);
          toast.error(getErrorMessage(error, 'Failed to load racks'));
        }
      } finally {
        if (!cancelled) setIsLoadingRacks(false);
      }
    };

    const loadRacksSummary = async () => {
      try {
        const response = await warehouseService.getAllRacksSummary(warehouseId);
        if (cancelled) return;
        
        // Create a map for quick lookup
        const summaryMap: Record<number, { totalLocations: number; occupiedCount: number; availableCount: number; locationSizeSqft: number | null; availableSqFt: number | null }> = {};
        const racksData = Array.isArray(response?.data) ? response.data : (response || []);

        racksData.forEach((rack: any) => {
          summaryMap[rack.rackId] = {
            totalLocations: rack.totalLocations || 0,
            occupiedCount: rack.occupiedCount || 0,
            availableCount: rack.availableCount || 0,
            locationSizeSqft: rack.locationSizeSqft ?? null,
            availableSqFt: rack.availableSqFt ?? null,
          };
        });
        
        setRackSummaryMap(summaryMap);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load racks summary:', error);
        }
      }
    };

    const loadZoneOccupancy = async () => {
      if (!warehouseId) {
        return;
      }
      
      try {
        const response = await warehouseService.getAllZonesOccupancy(warehouseId);
        if (cancelled) return;
        
        // Create a map for quick lookup
        const occupancyMap: Record<number, { totalPalletsStored: number; totalLocations: number; availableCount: number }> = {};

        // The response.data is the array directly
        const zonesData = Array.isArray(response?.data) ? response.data : (response || []);

        zonesData.forEach((zone: any) => {
          occupancyMap[zone.zoneId] = {
            totalPalletsStored: zone.totalPalletsStored || 0,
            totalLocations: zone.totalLocations || 0,
            availableCount: zone.availableCount || 0,
          };
        });
        
        setZoneOccupancyMap(occupancyMap);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load zone occupancy:', error);
          // Don't show error toast, this is optional enhancement
        }
      }
    };

    loadRacks();
    loadRacksSummary();
    loadZoneOccupancy();
    
    return () => {
      cancelled = true;
    };
  }, [warehouseId, toast]);

  const ensureLocationsLoaded = useCallback(async (rackId: number) => {
    if (locationsByRackId[rackId]) return;
    if (loadingLocationsByRackId[rackId]) return;

    setLoadingLocationsByRackId((prev) => ({ ...prev, [rackId]: true }));
    try {
      const response = await warehouseService.getRackLocationsWithOccupancy(rackId);

      // The warehouseService already extracts response.data
      // So response IS the data: { rackId, summary, locations }
      const locations = response?.locations || [];
      const summary = response?.summary;

      setLocationsByRackId((prev) => ({
        ...prev,
        [rackId]: locations,
      }));

      // Store rack summary (in case it wasn't loaded in bulk)
      if (summary) {
        setRackSummaryMap((prev) => ({
          ...prev,
          [rackId]: {
            totalLocations: summary.totalLocations || 0,
            occupiedCount: summary.occupiedCount || 0,
            availableCount: summary.availableCount || 0,
            locationSizeSqft: response?.locationSizeSqft ?? null,
            availableSqFt: summary.availableSqFt ?? null,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to load rack locations:', error);
      setLocationsByRackId((prev) => ({
        ...prev,
        [rackId]: [],
      }));
      toast.error(getErrorMessage(error, 'Failed to load rack locations'));
    } finally {
      setLoadingLocationsByRackId((prev) => ({ ...prev, [rackId]: false }));
    }
  }, [locationsByRackId, loadingLocationsByRackId, toast]);

  const ensureZoneLocationsLoaded = useCallback(async (zoneId: number) => {
    if (zoneLocationsByZoneId[zoneId]) return;
    if (loadingZoneLocationsByZoneId[zoneId]) return;

    setLoadingZoneLocationsByZoneId((prev) => ({ ...prev, [zoneId]: true }));
    try {
      const response = await warehouseService.getZoneLocationsWithOccupancy(zoneId);
      const locations = response?.locations || [];
      setZoneLocationsByZoneId((prev) => ({
        ...prev,
        [zoneId]: locations,
      }));
    } catch (error) {
      console.error('Failed to load zone locations:', error);
      setZoneLocationsByZoneId((prev) => ({
        ...prev,
        [zoneId]: [],
      }));
    } finally {
      setLoadingZoneLocationsByZoneId((prev) => ({ ...prev, [zoneId]: false }));
    }
  }, [zoneLocationsByZoneId, loadingZoneLocationsByZoneId]);

  // Auto-assign Ground placements: fills any still-blank row (zoneId === '')
  // with the first-come-first-serve available zone-location, once every
  // candidate zone's locations have loaded. Rows the user already touched
  // (manually or via a prior auto-fill) are left alone.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'ground') return;
    if (cardGroundPlacements.length === 0) return;
    if (!cardGroundPlacements.some((p) => p.zoneId === '')) return;

    const zonesForAssign = cardIsOversized ? heavyZonesSorted : normalZonesSorted;
    if (zonesForAssign.length === 0) return;

    const unloaded = zonesForAssign.filter((z: any) => !zoneLocationsByZoneId[z.id] && !loadingZoneLocationsByZoneId[z.id]);
    if (unloaded.length > 0) {
      unloaded.forEach((z: any) => void ensureZoneLocationsLoaded(z.id));
      return;
    }
    if (zonesForAssign.some((z: any) => loadingZoneLocationsByZoneId[z.id])) return;

    const goodQty = currentAssigningItem.goodQty || 0;
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(
      cardGroundPlacements.filter((p) => p.zoneLocationId).map((p) => p.zoneLocationId as number),
    );
    const containers = zonesForAssign.map((z: any) => ({ id: z.id, locations: zoneLocationsByZoneId[z.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardGroundPlacements.length, qtyPerPallet, goodQty, alreadyUsed);

    let changed = false;
    const next = cardGroundPlacements.map((row, idx) => {
      if (row.zoneId !== '') return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { ...row, zoneId: pick.containerId, zoneLocationId: pick.locationId, pallets: '1' };
    });
    if (changed) setCardGroundPlacements(next);
  }, [currentAssigningItem, cardStorageType, cardGroundPlacements, cardIsOversized, normalZonesSorted, heavyZonesSorted, zoneLocationsByZoneId, loadingZoneLocationsByZoneId, ensureZoneLocationsLoaded]);

  // Auto-assign Rack placements — same first-come-first-serve pattern as Ground.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'rack') return;
    if (cardRackPlacements.length === 0) return;
    if (!cardRackPlacements.some((p) => p.rackId === null)) return;
    if (racksSorted.length === 0) return;

    const unloaded = racksSorted.filter((r: any) => !locationsByRackId[r.id] && !loadingLocationsByRackId[r.id]);
    if (unloaded.length > 0) {
      unloaded.forEach((r: any) => void ensureLocationsLoaded(r.id));
      return;
    }
    if (racksSorted.some((r: any) => loadingLocationsByRackId[r.id])) return;

    const goodQty = currentAssigningItem.goodQty || 0;
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(
      cardRackPlacements.filter((p) => p.locationId).map((p) => p.locationId as number),
    );
    const containers = racksSorted.map((r: any) => ({ id: r.id, locations: locationsByRackId[r.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardRackPlacements.length, qtyPerPallet, goodQty, alreadyUsed);

    let changed = false;
    const next = cardRackPlacements.map((row, idx) => {
      if (row.rackId !== null) return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { rackId: pick.containerId, locationId: pick.locationId };
    });
    if (changed) setCardRackPlacements(next);
  }, [currentAssigningItem, cardStorageType, cardRackPlacements, racksSorted, locationsByRackId, loadingLocationsByRackId, ensureLocationsLoaded]);

  // Auto-assign LBD (damaged-stock) placements — Dedicated mode only; Shared
  // mode references an existing pallet elsewhere and has no per-row slot to
  // pick. Driven by CDAM+DAM qty, never Good Qty.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardLbdMode !== 'dedicated') return;
    if (cardLbdPlacementType !== 'zone') return;
    if (cardLbdPlacements.length === 0) return;
    if (!cardLbdPlacements.some((p) => p.zoneId === '')) return;
    if (lbdZonesSorted.length === 0) return;

    const unloaded = lbdZonesSorted.filter((z: any) => !zoneLocationsByZoneId[z.id] && !loadingZoneLocationsByZoneId[z.id]);
    if (unloaded.length > 0) {
      unloaded.forEach((z: any) => void ensureZoneLocationsLoaded(z.id));
      return;
    }
    if (lbdZonesSorted.some((z: any) => loadingZoneLocationsByZoneId[z.id])) return;

    const damagedQty = (currentAssigningItem.cdamQty || 0) + (currentAssigningItem.damQty || 0);
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(
      cardLbdPlacements.filter((p) => p.zoneLocationId).map((p) => p.zoneLocationId as number),
    );
    const containers = lbdZonesSorted.map((z: any) => ({ id: z.id, locations: zoneLocationsByZoneId[z.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardLbdPlacements.length, qtyPerPallet, damagedQty, alreadyUsed);

    let changed = false;
    const next = cardLbdPlacements.map((row, idx) => {
      if (row.zoneId !== '') return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { ...row, zoneId: pick.containerId, zoneLocationId: pick.locationId, pallets: '1' };
    });
    if (changed) setCardLbdPlacements(next);
  }, [currentAssigningItem, cardLbdMode, cardLbdPlacementType, cardLbdPlacements, lbdZonesSorted, zoneLocationsByZoneId, loadingZoneLocationsByZoneId, ensureZoneLocationsLoaded]);

  // Load products from product_master for this client
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { api } = await import('../services/api');
        const res = await (api as any).get(`/master-data/products?clientId=${clientId}&page=1&limit=200`);
        if (!cancelled) setProductMasterList((res as any)?.data?.items ?? []);
      } catch {
        if (!cancelled) setProductMasterList([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clientId]);

  // Load units of measurement once — backs the Unit dropdown
  useEffect(() => {
    let cancelled = false;
    uomService.list({ page: 1, limit: 500 }).then((res) => {
      if (!cancelled) setUomList(res.items);
    }).catch(() => {
      if (!cancelled) setUomList([]);
    });
    return () => { cancelled = true; };
  }, []);

  // Load batches when a product master is selected
  const loadBatchesForProduct = async (productMasterId: number) => {
    try {
      const { api } = await import('../services/api');
      const res = await (api as any).get(`/master-data/batches?productMasterId=${productMasterId}&page=1&limit=100`);
      setBatchList((res as any)?.data?.items ?? []);
    } catch {
      setBatchList([]);
    }
  };

  // Initialize client location, GRN remarks and transport details from existing GRN data
  useEffect(() => {
    if (grnData?.data?.clientLocation && !clientLocation) setClientLocation(grnData.data.clientLocation);
    if (grnData?.data?.grnRemarks && !grnRemarks)         setGrnRemarks(grnData.data.grnRemarks);
    if (grnData?.data?.vehicleNo)       setVehicleNo(grnData.data.vehicleNo);
    if (grnData?.data?.driverName)      setDriverName(grnData.data.driverName);
    if (grnData?.data?.driverPhone)     setDriverPhone(grnData.data.driverPhone);
    if (grnData?.data?.transporterName) setTransporterName(grnData.data.transporterName);
    if (grnData?.data?.lrNumber)        setLrNumber(grnData.data.lrNumber);
    if (grnData?.data?.lrDate)          setLrDate(grnData.data.lrDate);
    // Only re-run when the fetched GRN changes; setters/state are intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnData?.data]);

  // Load client space information when client is available
  useEffect(() => {
    if (!clientId) {
      setClientSpaceInfo(null);
      return;
    }

    let cancelled = false;

    const loadSpaceInfo = async () => {
      setIsLoadingSpaceInfo(true);
      setSpaceInfoError(null);
      try {
        const spaceInfo = await clientService.getClientSpaceInfo(clientId);
        if (!cancelled) {
          setClientSpaceInfo(spaceInfo);
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error('Failed to load client space info:', error);
          setSpaceInfoError(getErrorMessage(error, 'Failed to load space information'));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSpaceInfo(false);
        }
      }
    };

    loadSpaceInfo();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Handle form input change
  const handleInputChange = (field: keyof ItemFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Validate form
  const validateForm = async (): Promise<string | null> => {
    if (!formData.productMasterId) return 'Product description is required';
    if (!formData.batchNo || !formData.batchNo.trim()) return 'Batch number is required';
    if (!formData.unitType) return 'Unit type is required';
    if (!formData.handlingType) return 'Handling type is required';
    if (actualQty <= 0) return 'Total quantity must be greater than 0';
    if (formData.lotNo && formData.lotNo.trim().length > 20) return 'Lot No must not exceed 20 characters';

    // Validate datetime fields
    if (formData.expiryDate && !formData.mfgDate) {
      return 'Manufacturing date is required when expiry date is provided';
    }

    if (formData.mfgDate && formData.expiryDate) {
      const mfgDateTime = new Date(formData.mfgDate);
      const expiryDateTime = new Date(formData.expiryDate);
      
      if (isNaN(mfgDateTime.getTime())) {
        return 'Invalid manufacturing date format';
      }
      
      if (isNaN(expiryDateTime.getTime())) {
        return 'Invalid expiry date format';
      }
      
      if (expiryDateTime <= mfgDateTime) {
        return 'Expiry date must be greater than manufacturing date';
      }
    }

    return null;
  };

  // Helper function to populate form from an existing item
  const populateFormFromItem = (item: any, options: { clearBatchFields?: boolean } = {}) => {
    const { clearBatchFields = false } = options;
    
    // Find productTypeId by matching productDescription with product type name
    let productTypeId = null;
    if (item.productCategoryId && item.productDescription) {
      const allProductTypes = clientData?.productTypes || [];
      const matchingType = allProductTypes.find(
        (type: any) => type.categoryId === item.productCategoryId && type.name === item.productDescription
      );
      productTypeId = matchingType?.id || null;
    }
    
    // Load batches if productMasterId is set
    if (item.productMasterId) {
      loadBatchesForProduct(item.productMasterId);
    }

    setFormData({
      productCategoryId: item.productCategoryId ?? null,
      productTypeId: productTypeId,
      productMasterId: item.productMasterId ?? null,
      productDescription: item.productDescription ?? '',
      itemCode: clearBatchFields ? '' : (item.itemCode || ''),
      unitType: item.unitType,
      weightPerItem: item.weightPerItem?.toString() || '',
      batchNo: clearBatchFields ? '' : (item.batchNo || ''),
      skuCode: item.skuCode || '',
      customerSapCode: item.customerSapCode || '',
      mfgDate: clearBatchFields ? '' : (item.mfgDate ? formatDateTimeLocal(item.mfgDate) : ''),
      expiryDate: clearBatchFields ? '' : (item.expiryDate ? formatDateTimeLocal(item.expiryDate) : ''),
      expectedQty: (item.goodQty + item.cdamQty + item.damQty - item.shortExcess).toString(),
      goodQty: item.goodQty.toString(),
      cdamQty: item.cdamQty.toString(),
      damQty: item.damQty.toString(),
      palletCount: item.palletCount.toString(),
      requiredPalletCount: item.requiredPalletCount ? item.requiredPalletCount.toString() : '',
      remarks: item.remarks || '',
      grossWeightPerItem: (() => {
        const totalQty = (item.goodQty || 0) + (item.cdamQty || 0) + (item.damQty || 0);
        if (item.grossTotalWeight && totalQty > 0) return (parseFloat(item.grossTotalWeight) / totalQty).toFixed(4);
        return '';
      })(),
      lotNo: item.lotNo || '',
      handlingType: (item.handlingType as 'manual' | 'mhe' | '') || '',
    });

    return productTypeId;
  };

  // Edit item - populate form with item data
  const handleEditItem = async (item: any) => {
    setEditingItemId(item.id);
    setPalletMode(item.palletCount === 0 ? 'shared' : 'own');
    populateFormFromItem(item, { clearBatchFields: false });
    setStorageType(item.storageType);
    
    // Populate storage details
    if (item.storageType === 'ground' && item.storageDetails) {
      setGroundPlacements(item.storageDetails.map((s: any) => ({
        zoneId: s.zoneId,
        pallets: s.pallets.toString(),
      })));
    } else if (item.storageType === 'rack' && item.storageDetails) {
      const rackPlacements = item.storageDetails.map((s: any) => ({
        rackId: s.rackId,
        locationId: s.locationId,
      }));
      setRackPlacements(rackPlacements);
      
      // Pre-load locations for all racks used in storage details
      const rackIds = rackPlacements.map((p: any) => p.rackId).filter(Boolean) as number[];
      const uniqueRackIds = Array.from(new Set(rackIds));
      for (const rackId of uniqueRackIds) {
        await ensureLocationsLoaded(rackId);
      }
    }
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Duplicate item - copy all fields including batch/item code/dates + storage assignment
  const handleDuplicateItem = async (item: any) => {
    setEditingItemId(null);
    setDuplicatingFromItem(item);

    // Copy every field (no fields skipped)
    populateFormFromItem(item, { clearBatchFields: false });

    // Copy storage assignment exactly as-is
    setStorageType(item.storageType);
    setSelectedZoneId('');
    if (item.storageType === 'ground' && item.storageDetails) {
      setGroundPlacements(item.storageDetails.map((s: any) => ({
        zoneId: s.zoneId,
        pallets: s.pallets.toString(),
      })));
      setRackPlacements([]);
    } else if (item.storageType === 'rack' && item.storageDetails) {
      const newRackPlacements = item.storageDetails.map((s: any) => ({
        rackId: s.rackId,
        locationId: s.locationId,
      }));
      setRackPlacements(newRackPlacements);
      setGroundPlacements([]);
      const uniqueRackIds = Array.from(new Set(newRackPlacements.map((p: any) => p.rackId).filter(Boolean) as number[]));
      for (const rackId of uniqueRackIds) {
        await ensureLocationsLoaded(rackId);
      }
    } else {
      setGroundPlacements([]);
      setRackPlacements([]);
    }

    // Scroll to the form — delay lets React flush state before measuring
    setTimeout(() => {
      itemFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  // Add item
  const handleAddItem = async () => {
    const error = await validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    if (!grnId) {
      toast.error('GRN not initialized');
      return;
    }

    try {
      const payload = {
        productMasterId: formData.productMasterId!,
        itemCode: formData.itemCode.trim() || undefined,
        unitType: formData.unitType,
        weightPerItem: formData.weightPerItem ? parseFloat(formData.weightPerItem) : undefined,
        goodQty: parseInt(formData.goodQty) || 0,
        cdamQty: parseInt(formData.cdamQty) || 0,
        damQty: parseInt(formData.damQty) || 0,
        palletCount: 0,
        requiredPalletCount: parseInt(formData.requiredPalletCount) || (selectedBatchQtyPerPallet && goodQtyNumber > 0 ? Math.ceil(goodQtyNumber / selectedBatchQtyPerPallet) : undefined),
        storageType: 'ground' as const,
        storageDetails: [],
        shortExcess,
        totalWeight: parseFloat(totalWeight),
        grossTotalWeight: grossTotalWeight ? parseFloat(grossTotalWeight) : undefined,
        lotNo: formData.lotNo.trim() || undefined,
        handlingType: (formData.handlingType as 'manual' | 'mhe') || undefined,
        qtyPerPallet: selectedBatchQtyPerPallet ?? undefined,
        batchNo: formData.batchNo.trim() || undefined,
        skuCode: formData.skuCode.trim() || undefined,
        customerSapCode: formData.customerSapCode.trim() || undefined,
        mfgDate: formData.mfgDate || undefined,
        expiryDate: formData.expiryDate || undefined,
        remarks: formData.remarks.trim() || undefined,
      };

      if (editingItemId) {
        await updateItemMutation.mutateAsync({
          grnId,
          itemId: editingItemId,
          payload,
        });
      } else {
        await addItemMutation.mutateAsync({ grnId, payload });
      }

      // Clear form
      setFormData(initialFormData);
      setProductSearch('');
      setBatchList([]);
      setStorageType('ground');
      setSelectedZoneId('');
      setGroundPlacements([]);
      setRackPlacements([]);
      setEditingItemId(null);
      setPalletMode('own');
      setDuplicatingFromItem(null);
      toast.success(editingItemId ? 'Item updated successfully' : 'Item added successfully');
    } catch (error: any) {
      toast.error(getErrorMessage(error, editingItemId ? 'Failed to update item' : 'Failed to add item'));
    }
  };

  // Confirm GRN
  const handleConfirmGrn = async () => {
    if (items.length === 0) {
      toast.error('Please add at least one item before confirming');
      return;
    }

    // Pallet/zone is assigned per item from the products table (Pallet button).
    // Block submit until every product has a pallet assigned.
    const hasUnassigned = items.some(
      (item: any) =>
        !(
          item.palletCount > 0 ||
          (Array.isArray(item.storageDetails) && item.storageDetails.length > 0)
        ),
    );
    if (hasUnassigned) {
      toast.error('Please assign Pallet');
      return;
    }

    // Damaged / c-damaged stock must be assigned to an LBD zone before confirming.
    const hasUnassignedLbd = items.some((item: any) => {
      const damagedQty = (item.cdamQty || 0) + (item.damQty || 0);
      const lbdAssigned =
        !!item.lbdStorageMode &&
        Array.isArray(item.lbdStorageDetails) &&
        item.lbdStorageDetails.length > 0;
      return damagedQty > 0 && !lbdAssigned;
    });
    if (hasUnassignedLbd) {
      toast.error('Assign an LBD zone or rack for the damaged stock');
      return;
    }

    const lrError = FIELD_VALIDATORS.lrNumber(lrNumber);
    if (lrError) {
      setLrNumberError(lrError);
      toast.error(lrError);
      return;
    }

    if (lrDate && lrDate > todayStr()) {
      toast.error('LR Date cannot be in the future');
      return;
    }

    const confirmed = await confirm({
      title: 'Confirm GRN',
      message: 'Are you sure you want to confirm this GRN? This action cannot be undone.',
      confirmText: 'Confirm GRN',
      variant: 'primary',
    });

    if (!confirmed) return;

    try {
      await confirmGrnMutation.mutateAsync({
        grnId: grnId!,
        clientLocation,
        grnRemarks,
        vehicleNo:       vehicleNo       || undefined,
        driverName:      driverName      || undefined,
        driverPhone:     driverPhone     || undefined,
        transporterName: transporterName || undefined,
        lrNumber:        lrNumber        || undefined,
        lrDate:          lrDate          || undefined,
      });
      toast.success('GRN confirmed successfully');
      navigate('/inward');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to confirm GRN'));
    }
  };

  // Delete item
  const handleDeleteItem = async (itemId: number) => {
    const confirmed = await confirm({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    if (!grnId) {
      toast.error('GRN not initialized');
      return;
    }

    try {
      await deleteItemMutation.mutateAsync({ grnId, itemId });
      toast.success('Item deleted successfully');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to delete item'));
    }
  };

  // Reset the per-product pallet assignment panel (good + LBD storage).
  const resetCardAssignment = () => {
    setAssigningPalletForItemId(null);
    setCardPalletCount('');
    setCardGroundPlacements([]);
    setCardRackPlacements([]);
    setCardPalletMode('own');
    setCardLbdMode('dedicated');
    setCardLbdZoneId('');
    setCardLbdPlacements([{ zoneId: '', pallets: '1', zoneLocationId: null }]);
    setCardLbdRemark('');
    setCardLbdSharedPalletRef('');
    setCardIsOversized(false);
  };

  // Save pallet assignment from product card
  const handleSavePalletAssignment = async (item: any) => {
    if (!grnId) return;
    const itemId = item.id;

    const isShared = cardPalletMode === 'shared';
    const palletCount = isShared ? 0 : parseInt(cardPalletCount) || 0;
    // Shared pallets claim 0 pallets but still record one location.
    // Dedicated ground placements now require a specific zone location, same
    // as rack requires rackId + locationId — no more "No specific slot" bulk
    // placement. Shared-pallet mode is exempt: its ground UI only offers a
    // Zone picker, with no location dropdown to fill this with.
    const storageDetails =
      cardStorageType === 'ground'
        ? cardGroundPlacements
            .filter((p) => p.zoneId !== '' && (isShared || p.zoneLocationId))
            .map((p) => ({
              zoneId: p.zoneId as number,
              pallets: isShared ? 0 : parseInt(p.pallets) || 0,
              zoneLocationId: p.zoneLocationId ?? undefined,
            }))
        : cardRackPlacements
            .filter((p) => p.rackId && p.locationId)
            .map((p) => ({ rackId: p.rackId!, locationId: p.locationId! }));

    if (!isShared && palletCount < 1) {
      toast.error('Enter a pallet count of at least 1');
      return;
    }
    if (!isShared && item.requiredPalletCount <= 0) {
      const manualPalletCapacity = cardStorageType === 'rack' ? warehouseRackCapacity : warehouseZoneCapacity;
      if (palletCount > manualPalletCapacity) {
        toast.error(`Only ${manualPalletCapacity} pallet${manualPalletCapacity !== 1 ? 's' : ''} of ${cardStorageType === 'rack' ? 'rack' : 'zone'} space available in this warehouse — reduce the pallet count`);
        return;
      }
    }
    if (storageDetails.length === 0) {
      toast.error(isShared ? 'Select a location for the shared pallet' : 'Assign a storage location');
      return;
    }
    if (!isShared && cardStorageType === 'ground' && cardGroundPlacements.some((p) => p.zoneId !== '' && !p.zoneLocationId)) {
      toast.error('Select a specific location for every zone row');
      return;
    }

    const requiredPallets = (!isShared && item.requiredPalletCount > 0) ? item.requiredPalletCount : palletCount;
    if (!isShared && cardStorageType === 'rack' && storageDetails.length !== requiredPallets) {
      const diff = requiredPallets - storageDetails.length;
      if (diff > 0) {
        toast.error(`You require ${requiredPallets} pallet${requiredPallets > 1 ? 's' : ''}, please add ${diff} more pallet${diff > 1 ? 's' : ''}`);
      } else {
        toast.error(`You require ${requiredPallets} pallet${requiredPallets > 1 ? 's' : ''}, remove ${Math.abs(diff)} pallet${Math.abs(diff) > 1 ? 's' : ''}`);
      }
      return;
    }
    if (!isShared && cardStorageType === 'ground') {
      const totalGroundPallets = (storageDetails as Array<{ pallets: number }>).reduce((s, p) => s + (p.pallets || 0), 0);
      if (totalGroundPallets !== requiredPallets) {
        const diff = requiredPallets - totalGroundPallets;
        if (diff > 0) {
          toast.error(`You require ${requiredPallets} pallet${requiredPallets > 1 ? 's' : ''}, ${diff} more pallet${diff > 1 ? 's' : ''} needed across your zones`);
        } else {
          toast.error(`You require ${requiredPallets} pallet${requiredPallets > 1 ? 's' : ''}, remove ${Math.abs(diff)} pallet${Math.abs(diff) > 1 ? 's' : ''} from your zones`);
        }
        return;
      }
    }

    // Oversized / heavy pallets may only sit in a Heavy zone — sized the same
    // as a regular pallet (no custom size collected).
    const oversized = cardPalletMode === 'own' && cardIsOversized;
    const palletSizeSqft = 25;
    if (oversized) {
      if (cardStorageType !== 'ground') {
        toast.error('Oversized pallets must use ground (Heavy zone) storage');
        return;
      }
      const allHeavy = (storageDetails as Array<{ zoneId?: number }>).every((s) =>
        heavyZones.some((z: any) => z.id === s.zoneId),
      );
      if (!allHeavy) {
        toast.error('Oversized pallets can only be stored in a Heavy zone');
        return;
      }
    }

    // Damaged & c-damaged stock must be stored separately in an LBD zone.
    const damagedQty = (item.cdamQty || 0) + (item.damQty || 0);
    let lbdFields: Partial<CreateGrnItemPayload>;
    if (damagedQty > 0) {
      if (cardLbdMode === 'shared') {
        if (cardLbdZoneId === '') {
          toast.error('Select an LBD zone for the damaged stock');
          return;
        }
        const ref = cardLbdSharedPalletRef.trim();
        if (!ref) {
          toast.error('Enter a shared pallet reference for the LBD stock');
          return;
        }
        lbdFields = {
          lbdStorageMode: 'shared',
          lbdPalletCount: 0,
          lbdRemark: cardLbdRemark.trim() || null,
          lbdSharedPalletRef: ref,
          lbdStorageDetails: [{ zoneId: cardLbdZoneId as number, sharedPalletRef: ref }],
        };
      } else if (cardLbdPlacementType === 'rack') {
        if (cardLbdPlacements.some((p) => !p.rackId || !p.locationId)) {
          toast.error('Select a rack and location for every damaged-stock pallet row');
          return;
        }
        const lbdPallets = cardLbdPlacements.length;
        if (lbdPallets < 1) {
          toast.error('Add at least one LBD rack location');
          return;
        }
        lbdFields = {
          lbdStorageMode: 'dedicated',
          lbdPalletCount: lbdPallets,
          lbdRemark: cardLbdRemark.trim() || null,
          lbdSharedPalletRef: null,
          lbdStorageDetails: cardLbdPlacements.map((p) => ({
            rackId: p.rackId as number,
            locationId: p.locationId as number,
          })),
        };
      } else {
        if (cardLbdPlacements.some((p) => p.zoneId === '')) {
          toast.error('Select an LBD zone for every damaged-stock pallet row');
          return;
        }
        if (cardLbdPlacements.some((p) => p.zoneId !== '' && !p.zoneLocationId)) {
          toast.error('Select a specific location for every LBD zone row');
          return;
        }
        const lbdPallets = cardLbdPlacements.reduce((sum, p) => sum + (parseInt(p.pallets) || 0), 0);
        if (lbdPallets < 1) {
          toast.error('Enter an LBD pallet count of at least 1');
          return;
        }
        lbdFields = {
          lbdStorageMode: 'dedicated',
          lbdPalletCount: lbdPallets,
          lbdRemark: cardLbdRemark.trim() || null,
          lbdSharedPalletRef: null,
          lbdStorageDetails: cardLbdPlacements.map((p) => ({
            zoneId: p.zoneId as number,
            pallets: parseInt(p.pallets) || 0,
            zoneLocationId: p.zoneLocationId ?? undefined,
          })),
        };
      }
    } else {
      // No damaged stock — clear any stale LBD assignment.
      lbdFields = {
        lbdStorageMode: null,
        lbdPalletCount: null,
        lbdRemark: null,
        lbdSharedPalletRef: null,
        lbdStorageDetails: null,
      };
    }

    try {
      await updateItemMutation.mutateAsync({
        grnId,
        itemId,
        payload: { palletCount, requiredPalletCount: requiredPallets, storageType: cardStorageType, storageDetails, isOversized: oversized, palletSizeSqft, ...lbdFields },
      });

      // Drop the cached location lists for every zone/rack this save just
      // touched — ensureZoneLocationsLoaded/ensureLocationsLoaded only fetch
      // once and cache for the rest of the page's lifetime, so without this
      // the NEXT product's Assign Pallet card would keep showing these
      // locations as free (pre-save) and let the same slot be picked twice.
      const usedZoneIds = new Set<number>();
      const usedRackIds = new Set<number>();
      for (const s of storageDetails as Array<{ zoneId?: number; rackId?: number }>) {
        if (s.zoneId) usedZoneIds.add(s.zoneId);
        if (s.rackId) usedRackIds.add(s.rackId);
      }
      for (const d of (lbdFields.lbdStorageDetails as Array<{ zoneId?: number; rackId?: number }> | null) ?? []) {
        if (d.zoneId) usedZoneIds.add(d.zoneId);
        if (d.rackId) usedRackIds.add(d.rackId);
      }
      if (usedZoneIds.size > 0) {
        setZoneLocationsByZoneId((prev) => {
          const next = { ...prev };
          usedZoneIds.forEach((id) => { delete next[id]; });
          return next;
        });
      }
      if (usedRackIds.size > 0) {
        setLocationsByRackId((prev) => {
          const next = { ...prev };
          usedRackIds.forEach((id) => { delete next[id]; });
          return next;
        });
        setRackSummaryMap((prev) => {
          const next = { ...prev };
          usedRackIds.forEach((id) => { delete next[id]; });
          return next;
        });
      }

      resetCardAssignment();
      toast.success('Pallet assignment saved');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to save pallet assignment'));
    }
  };

  // Assign multiple products to a shared pallet
  const handleAssignSharedPallet = async () => {
    if (!grnId || selectedForPalletIds.length < 2) return;
    try {
      const storageDetails =
        sharedAssignStorageType === 'ground'
          ? sharedAssignZoneId !== '' ? [{ zoneId: sharedAssignZoneId as number, pallets: 0 }] : []
          : sharedAssignRackId && sharedAssignLocationId ? [{ rackId: sharedAssignRackId, locationId: sharedAssignLocationId }] : [];
      for (const itemId of selectedForPalletIds) {
        await updateItemMutation.mutateAsync({
          grnId,
          itemId,
          payload: { palletCount: 0, storageType: sharedAssignStorageType, storageDetails },
        });
      }
      setSelectedForPalletIds([]);
      setSharedAssignZoneId('');
      setSharedAssignRackId(null);
      setSharedAssignLocationId(null);
      toast.success(`${selectedForPalletIds.length} products assigned to shared pallet`);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to assign shared pallet'));
    }
  };

  // Check GRN status - handle both nested and direct response structures
  const currentStatus = grnData?.data?.status || grnData?.status;
  const isDraft = currentStatus === 'draft';
  const isSubmitted = currentStatus === 'submitted';
  const isApproved = currentStatus === 'approved';
  const isRejected = currentStatus === 'rejected';
  // An approved GRN reopened for a past-edit: relax create-flow pallet locks so
  // existing items can be deleted/reassigned/re-edited.
  const isReopenedForEdit = Boolean(
    grnData?.data?.reopenedForEdit ?? grnData?.reopenedForEdit,
  );

  // Loading state - wait for initialization AND GRN data
  if (isInitializing || (grnId && !grnData)) {
    return (
      <Layout>
        <div className="min-h-screen p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading GRN...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="mb-6 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-100 p-4 lg:p-6 rounded-2xl shadow-xl border-2 border-brand-300 hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center space-x-3 lg:space-x-4 min-w-0">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {(isPastEdit || isReopenedForEdit) ? 'Edit Past GRN' : 'Create GRN'}
                </h2>
                <p className="text-gray-700 mt-1 font-medium">
                  {(isPastEdit || isReopenedForEdit)
                    ? 'Editing an approved GRN — resubmit for approval'
                    : 'Goods Receipt Note - Inbound Processing'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/inward')}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-semibold shadow-lg hover:shadow-xl transform lg:hover:scale-105 transition-all duration-300 flex items-center space-x-2 border-2 border-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Inbound</span>
            </button>
          </div>
        </div>

        {/* Past GRN re-edit banner */}
        {isReopenedForEdit && (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" /></svg>
            <p className="text-sm text-amber-800">
              You are re-editing an <strong>approved (past) GRN</strong>. Its inventory was reversed when reopened.
              Change products, edit items, or reassign pallets as needed, then <strong>Resubmit for Approval</strong> —
              inventory is re-recorded on re-approval.
            </p>
          </div>
        )}

        {/* Status Bar */}
        {(grnData?.data || (grnData?.id && grnData?.status)) && (
          <div className={`mb-4 p-4 rounded-xl flex flex-wrap justify-between items-center gap-2 shadow-lg transform transition-all duration-300 hover:scale-[1.01] ${
            isSubmitted ? 'bg-yellow-50 border border-yellow-200' :
            isApproved ? 'bg-green-50 border border-green-200' :
            isRejected ? 'bg-red-50 border border-red-200' :
            'bg-brand-50 border border-brand-200'
          }`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <span className={`font-semibold ${
                isSubmitted ? 'text-yellow-700' :
                isApproved ? 'text-green-700' :
                isRejected ? 'text-red-700' :
                'text-brand-700'
              }`}>
                {isSubmitted && 'Submitted for approval'}
                {isApproved && 'Approved'}
                {isRejected && `Rejected${(grnData?.data?.rejectionReason || grnData?.rejectionReason) ? ` - ${grnData?.data?.rejectionReason || grnData?.rejectionReason}` : ''}`}
                {isDraft && 'Draft'}
                {!isDraft && !isSubmitted && !isApproved && !isRejected && (currentStatus || 'Unknown')}
              </span>
              {isApproved && approverData && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600">
                    Approved by: <span className="font-semibold text-gray-900">{approverData.name || approverData.email}</span>
                  </span>
                  {(grnData?.data?.approvedAt || grnData?.approvedAt) && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">
                        {new Date(grnData?.data?.approvedAt || grnData?.approvedAt).toLocaleString()}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            {(grnData?.data?.grnNumber || grnData?.grnNumber) && (
              <span className="text-sm text-gray-600">
                GRN: <span className="font-semibold">{grnData?.data?.grnNumber || grnData?.grnNumber}</span>
              </span>
            )}
          </div>
        )}

        {/* GRN Header - Read Only */}
        <div className="card mb-6 transform transition-all duration-300 hover:shadow-xl animate-fade-in">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg mr-3 shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Gate Entry Details</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 bg-gradient-to-r from-brand-50 to-brand-50 p-5 rounded-xl border-2 border-brand-200 shadow-sm">
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Entry Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={gateEntry?.entryNumber || grnData?.data?.gateEntryNumber || '-'}>
                {gateEntry?.entryNumber || grnData?.data?.gateEntryNumber || '-'}
              </p>
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Client Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={gateEntry?.clientName || grnData?.data?.clientName || '-'}>
                {gateEntry?.clientName || grnData?.data?.clientName || '-'}
              </p>
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Vehicle Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={gateEntry?.vehicleNumber || '-'}>
                {gateEntry?.vehicleNumber || '-'}
              </p>
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Expected Qty</p>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {gateEntry?.expectedQuantity || '-'}
              </p>
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Dock Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={gateEntry?.dockNumber || '-'}>
                {gateEntry?.dockNumber || '-'}
              </p>
            </div>
            <div className="min-w-0 overflow-hidden">
              <p className="text-xs text-brand-600 font-medium mb-1">Invoice Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={gateEntry?.invoiceNumber || '-'}>
                {gateEntry?.invoiceNumber || '-'}
              </p>
            </div>
          </div>
          {grnData?.data?.grnNumber && (
            <div className="mt-4 p-3 bg-gray-50 border-2 border-gray-300 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">GRN Number:</span> {grnData.data.grnNumber}
              </p>
            </div>
          )}
        </div>

        {/* Storage Space Availability Container */}
        {clientId && (
          <div className="card mb-6 border-2 border-gray-300">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Storage Space Availability
            </h3>

            {isLoadingSpaceInfo ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mr-3"></div>
                <span className="text-gray-600">Loading space information...</span>
              </div>
            ) : spaceInfoError ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Unable to load space information</p>
                    <p className="text-xs text-yellow-700 mt-1">{spaceInfoError}</p>
                  </div>
                </div>
              </div>
            ) : clientSpaceInfo ? (
              <div>
                {/* Both-type clients: show rack (pallets) and zone (sqft) independently */}
                {clientSpaceInfo.clientType === 'both' && (
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border-2 border-brand-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider">Racks (Pallets)</p>
                        <span className="text-xs font-bold text-brand-900">{clientSpaceInfo.rackUtilizationPercentage}%</span>
                      </div>
                      <p className="text-2xl font-bold text-brand-900">
                        {Math.max(0, clientSpaceInfo.rackPalletsAvailable).toLocaleString()}
                        <span className="text-sm font-medium text-gray-500"> pallets free</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {clientSpaceInfo.rackPalletsUsed.toLocaleString()} / {clientSpaceInfo.rackPalletLimit.toLocaleString()} used · extra pallets bill as additional space
                      </p>
                    </div>
                    <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Zones (Sq Ft)</p>
                        <span className="text-xs font-bold text-green-900">{clientSpaceInfo.zoneUtilizationPercentage}%</span>
                      </div>
                      <p className="text-2xl font-bold text-green-900">
                        {Math.max(0, clientSpaceInfo.zoneSqftAvailable).toLocaleString()}
                        <span className="text-sm font-medium text-gray-500"> sqft free</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {clientSpaceInfo.zoneSqftUsed.toLocaleString()} / {clientSpaceInfo.zoneSqftLimit.toLocaleString()} used · extra sqft bills as additional space
                      </p>
                    </div>
                  </div>
                )}
                {/* Client Utilization Bar */}
                <div className="mb-4 bg-gradient-to-r from-brand-50 to-brand-50 rounded-lg p-4 border-2 border-brand-300">
                  <div className="flex flex-wrap items-center justify-between mb-3 gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg className="w-5 h-5 text-brand-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                      </svg>
                      <h4 className="text-base font-bold text-gray-900">
                        Client Utilization
                      </h4>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 min-w-0">
                      <div className="text-right min-w-0">
                        <p className="text-xs text-gray-500">Client</p>
                        <p className="text-sm font-semibold text-gray-900 truncate max-w-[160px] lg:max-w-[200px]" title={clientSpaceInfo.clientName}>
                          {clientSpaceInfo.clientName}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-500">Utilization</p>
                        <p className={`text-2xl font-bold ${
                          clientSpaceInfo.utilizationPercentage >= 100 
                            ? 'text-red-600' 
                            : clientSpaceInfo.utilizationPercentage >= 90
                              ? 'text-yellow-600'
                              : clientSpaceInfo.utilizationPercentage >= 75
                                ? 'text-orange-600'
                                : 'text-green-600'
                        }`}>
                          {clientSpaceInfo.utilizationPercentage}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden shadow-inner">
                      <div 
                        className={`h-8 rounded-full transition-all duration-500 flex items-center justify-end pr-3 ${
                          clientSpaceInfo.utilizationPercentage >= 100 
                            ? 'bg-gradient-to-r from-red-500 to-red-600' 
                            : clientSpaceInfo.utilizationPercentage >= 90
                              ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
                              : clientSpaceInfo.utilizationPercentage >= 75
                                ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                                : 'bg-gradient-to-r from-green-500 to-green-600'
                        }`}
                        style={{ width: `${Math.min(clientSpaceInfo.utilizationPercentage, 100)}%` }}
                      >
                        {clientSpaceInfo.utilizationPercentage > 10 && (
                          <span className="text-white text-xs font-bold">
                            {isPerPalletClient(clientSpaceInfo.clientType) 
                              ? `${clientSpaceInfo.palletsUsed.toLocaleString()} / ${clientSpaceInfo.totalAllocatedPallets.toLocaleString()} pallets`
                              : `${clientSpaceInfo.spaceUsed.toLocaleString()} / ${clientSpaceInfo.totalAllocated.toLocaleString()} sqft`
                            }
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Markers for 25%, 50%, 75% */}
                    <div className="absolute top-0 left-1/4 w-px h-8 bg-gray-400 opacity-30"></div>
                    <div className="absolute top-0 left-1/2 w-px h-8 bg-gray-400 opacity-30"></div>
                    <div className="absolute top-0 left-3/4 w-px h-8 bg-gray-400 opacity-30"></div>
                  </div>
                </div>

                {/* Space Overview - 3 Cards Only */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-brand-50 to-brand-100 p-4 rounded-lg border border-brand-200">
                    <p className="text-xs text-brand-600 font-medium mb-1">Total Allocated</p>
                    <p className="text-2xl font-bold text-brand-900">
                      {clientSpaceInfo.clientType === 'per_pallet' 
                        ? clientSpaceInfo.totalAllocatedPallets.toLocaleString()
                        : clientSpaceInfo.totalAllocated.toLocaleString()
                      }
                    </p>
                    <p className="text-xs text-brand-600 mt-1">
                      {clientSpaceInfo.clientType === 'per_pallet' ? 'pallets' : 'sqft'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium mb-1">Space Used</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {clientSpaceInfo.clientType === 'per_pallet' 
                        ? clientSpaceInfo.palletsUsed.toLocaleString()
                        : clientSpaceInfo.spaceUsed.toLocaleString()
                      }
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      {clientSpaceInfo.clientType === 'per_pallet' ? 'pallets' : 'sqft'}
                    </p>
                  </div>
                  <div className={`bg-gradient-to-br p-4 rounded-lg border ${
                    (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) <= 0 
                      ? 'from-red-50 to-red-100 border-red-200' 
                      : (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable < 20 : clientSpaceInfo.spaceAvailable < 500)
                        ? 'from-yellow-50 to-yellow-100 border-yellow-200'
                        : 'from-green-50 to-green-100 border-green-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${
                      (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) <= 0 
                        ? 'text-red-600' 
                        : (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable < 20 : clientSpaceInfo.spaceAvailable < 500)
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}>Space Available</p>
                    <p className={`text-2xl font-bold ${
                      (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) <= 0 
                        ? 'text-red-900' 
                        : (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable < 20 : clientSpaceInfo.spaceAvailable < 500)
                          ? 'text-yellow-900'
                          : 'text-green-900'
                    }`}>
                      {clientSpaceInfo.clientType === 'per_pallet' 
                        ? clientSpaceInfo.palletsAvailable.toLocaleString()
                        : clientSpaceInfo.spaceAvailable.toLocaleString()
                      }
                    </p>
                    <p className={`text-xs mt-1 ${
                      (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) <= 0 
                        ? 'text-red-600' 
                        : (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable < 20 : clientSpaceInfo.spaceAvailable < 500)
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}>
                      {clientSpaceInfo.clientType === 'per_pallet' ? 'pallets' : 'sqft'}
                    </p>
                  </div>
                </div>

                {/* Additional Space Warning */}
                {clientSpaceInfo.isOverCapacity && (
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg mb-4">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-orange-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-orange-800 mb-1">
                          ⚠️ Client is Currently Using Additional Space
                        </h4>
                        <p className="text-sm text-orange-700">
                          This client has exceeded their allocated capacity by{' '}
                          <span className="font-bold">
                            {clientSpaceInfo.clientType === 'per_pallet' 
                              ? `${Math.floor(clientSpaceInfo.overCapacityAmount / 25).toLocaleString()} pallets`
                              : `${clientSpaceInfo.overCapacityAmount.toLocaleString()} sqft`
                            }
                          </span>.
                          The system is automatically tracking this as additional space usage.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Space Full Warning */}
                {!clientSpaceInfo.isOverCapacity && (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) <= 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-red-800 mb-1">
                          🚫 Storage Space Full
                        </h4>
                        <p className="text-sm text-red-700 mb-2">
                          This client has reached their allocated storage capacity. Any additional{' '}
                          {clientSpaceInfo.clientType === 'per_pallet' ? 'pallets' : 'space'} will be tracked as <span className="font-bold">additional space usage</span>.
                        </p>
                        <p className="text-xs text-red-600 bg-red-100 px-3 py-2 rounded">
                          <span className="font-semibold">Note:</span> The system will automatically track overflow space. You can proceed with the GRN, and the additional space will be recorded for billing and reporting purposes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Low Space Warning */}
                {!clientSpaceInfo.isOverCapacity && 
                 (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable : clientSpaceInfo.spaceAvailable) > 0 && 
                 (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.palletsAvailable < 20 : clientSpaceInfo.spaceAvailable < 500) && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-4">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                          ⚠️ Low Storage Space
                        </h4>
                        <p className="text-sm text-yellow-700">
                          Only{' '}
                          <span className="font-bold">
                            {clientSpaceInfo.clientType === 'per_pallet' 
                              ? `${clientSpaceInfo.palletsAvailable.toLocaleString()} pallets`
                              : `${clientSpaceInfo.spaceAvailable.toLocaleString()} sqft`
                            }
                          </span>{' '}
                          remaining. If this inward exceeds available space, the system will automatically track additional space usage.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Space Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Space Breakdown</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Base Allocation:</span>
                      <span className="font-semibold text-gray-900">
                        {clientSpaceInfo.clientType === 'per_pallet' 
                          ? `${clientSpaceInfo.baseAllocationPallets.toLocaleString()} pallets`
                          : `${clientSpaceInfo.baseAllocation.toLocaleString()} sqft`
                        }
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Additional Space Allocated:</span>
                      <span className={`font-semibold ${
                        (clientSpaceInfo.clientType === 'per_pallet' ? clientSpaceInfo.additionalSpaceAllocatedPallets : clientSpaceInfo.additionalSpaceAllocated) > 0 
                          ? 'text-orange-600' 
                          : 'text-gray-900'
                      }`}>
                        {clientSpaceInfo.clientType === 'per_pallet' 
                          ? `${clientSpaceInfo.additionalSpaceAllocatedPallets.toLocaleString()} pallets`
                          : `${clientSpaceInfo.additionalSpaceAllocated.toLocaleString()} sqft`
                        }
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Client Type:</span>
                      <span className="font-semibold text-gray-900 uppercase">{clientSpaceInfo.clientType.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Capacity Status:</span>
                      <span className={`font-semibold ${
                        clientSpaceInfo.isOverCapacity 
                          ? 'text-red-600' 
                          : clientSpaceInfo.utilizationPercentage >= 90
                            ? 'text-yellow-600'
                            : 'text-green-600'
                      }`}>
                        {clientSpaceInfo.isOverCapacity 
                          ? 'Over Capacity' 
                          : clientSpaceInfo.utilizationPercentage >= 90
                            ? 'Near Capacity'
                            : 'Normal'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Transport Details Section — editable */}
        {(isDraft || isRejected) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-truck text-orange-500 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Transport Details</h3>
                <p className="text-xs text-gray-500 mt-0.5">Vehicle and logistics information for this shipment</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-car text-orange-400 text-xs"></i>
                  Vehicle Number
                </label>
                <input
                  type="text"
                  value={vehicleNo}
                  readOnly
                  placeholder="Auto-filled from Security Check"
                  className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-600 placeholder-gray-400 cursor-default"
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-user text-orange-400 text-xs"></i>
                  Driver Name
                </label>
                <input
                  type="text"
                  value={driverName}
                  readOnly
                  placeholder="Auto-filled from Security Check"
                  className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-600 placeholder-gray-400 cursor-default"
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-phone text-orange-400 text-xs"></i>
                  Driver Phone
                </label>
                <input
                  type="tel"
                  value={driverPhone}
                  readOnly
                  placeholder="Auto-filled from Security Check"
                  className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-600 placeholder-gray-400 cursor-default"
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-building text-orange-400 text-xs"></i>
                  Transporter Name
                </label>
                <input
                  type="text"
                  value={transporterName}
                  onChange={e => setTransporterName(INPUT_SANITIZERS.companyNameFormat(e.target.value))}
                  placeholder="e.g. Fast Cargo Pvt Ltd"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm text-gray-800 placeholder-gray-400 transition-colors"
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-file-alt text-orange-400 text-xs"></i>
                  LR Number
                </label>
                <input
                  type="text"
                  value={lrNumber}
                  onChange={e => {
                    setLrNumber(INPUT_SANITIZERS.lrNumberFormat(e.target.value));
                    if (lrNumberError) setLrNumberError(null);
                  }}
                  onBlur={() => setLrNumberError(FIELD_VALIDATORS.lrNumber(lrNumber))}
                  placeholder="e.g. LR2026001234 or LR-2026-001234"
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 text-sm text-gray-800 placeholder-gray-400 transition-colors ${lrNumberError ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 focus:ring-orange-400 focus:border-orange-400'}`}
                />
                {lrNumberError && (
                  <p className="text-xs text-red-600 mt-1">{lrNumberError}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <i className="fas fa-calendar text-orange-400 text-xs"></i>
                  LR Date
                </label>
                <input
                  type="date"
                  value={lrDate}
                  max={todayStr()}
                  onChange={e => {
                    if (e.target.value > todayStr()) {
                      toast.error('LR Date cannot be in the future');
                      return;
                    }
                    setLrDate(e.target.value);
                  }}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm text-gray-800 transition-colors"
                />
              </div>
            </div>
          </div>
        )}

        {/* Transport Details — read-only display */}
        {(isSubmitted || isApproved) && (grnData?.data?.vehicleNo || grnData?.data?.driverName || grnData?.data?.transporterName || grnData?.data?.lrNumber) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-truck text-orange-500 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Transport Details</h3>
                <p className="text-xs text-gray-500 mt-0.5">Vehicle and logistics information</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {grnData.data.vehicleNo && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Vehicle No</p>
                  <p className="text-sm font-semibold text-gray-800">{grnData.data.vehicleNo}</p>
                </div>
              )}
              {grnData.data.driverName && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Driver Name</p>
                  <p className="text-sm text-gray-800">{grnData.data.driverName}</p>
                </div>
              )}
              {grnData.data.driverPhone && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Driver Phone</p>
                  <p className="text-sm text-gray-800">{grnData.data.driverPhone}</p>
                </div>
              )}
              {grnData.data.transporterName && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Transporter</p>
                  <p className="text-sm text-gray-800">{grnData.data.transporterName}</p>
                </div>
              )}
              {grnData.data.lrNumber && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">LR Number</p>
                  <p className="text-sm font-mono text-gray-800">{grnData.data.lrNumber}</p>
                </div>
              )}
              {grnData.data.lrDate && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">LR Date</p>
                  <p className="text-sm text-gray-800">{new Date(grnData.data.lrDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Inbound Information Section */}
        {(isDraft || isRejected) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-info-circle text-brand-600 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Inbound Information</h3>
                <p className="text-xs text-gray-500 mt-0.5">Optional details attached to this GRN</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="client-location">
                  <span aria-hidden="true">🚚</span>
                  Supplier Name
                </label>
                <div className="relative">
                  <textarea
                    id="client-location"
                    rows={4}
                    value={clientLocation}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sanitized = value.replace(/[^a-zA-Z0-9\s,.\-\n]/g, '');
                      if (sanitized.length <= 200) {
                        setClientLocation(sanitized);
                      }
                      setShowSupplierSuggestions(true);
                    }}
                    onFocus={() => setShowSupplierSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 150)}
                    placeholder="Supplier Name"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors"
                    maxLength={200}
                  />
                  {showSupplierSuggestions && supplierSuggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {supplierSuggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b border-gray-100 last:border-0 whitespace-pre-line"
                          onMouseDown={() => {
                            setClientLocation(suggestion);
                            setShowSupplierSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end items-center">
                  <span className={`text-xs font-medium tabular-nums ${clientLocation.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                    {clientLocation.length}/200
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="grn-remarks">
                  <i className="fas fa-sticky-note text-amber-400 text-xs"></i>
                  Additional Information
                </label>
                <div className="relative">
                  <textarea
                    id="grn-remarks"
                    rows={4}
                    value={grnRemarks}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sanitized = value.replace(/[^a-zA-Z0-9\s,.\-\n]/g, '');
                      if (sanitized.length <= 200) {
                        setGrnRemarks(sanitized);
                      }
                    }}
                    placeholder="Remarks, special instructions, or notes for this shipment..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors"
                    maxLength={200}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-400">Included with GRN records</p>
                  <span className={`text-xs font-medium tabular-nums ${grnRemarks.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                    {grnRemarks.length}/200
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Display Inbound Information if GRN is Submitted/Approved */}
        {(isSubmitted || isApproved) && (grnData?.data?.clientLocation || grnData?.data?.grnRemarks) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-info-circle text-brand-600 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Inbound Information</h3>
                <p className="text-xs text-gray-500 mt-0.5">Details attached to this GRN</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {grnData?.data?.clientLocation && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-map-marker-alt text-brand-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier Name</p>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap pl-8">{grnData.data.clientLocation}</p>
                </div>
              )}
              {grnData?.data?.grnRemarks && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-sticky-note text-amber-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Additional Information</p>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap pl-8">{grnData.data.grnRemarks}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {(clientError || zonesError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {clientError && (
              <p>{getErrorMessage(clientErrorData, 'Failed to load client data')}</p>
            )}
            {zonesError && (
              <p>{getErrorMessage(zonesErrorData, 'Failed to load zones')}</p>
            )}
          </div>
        )}

        {/* Item Form */}
        {(isDraft || isRejected) && (
          <div ref={itemFormRef} className={`card mb-6 ${duplicatingFromItem ? 'border-2 border-purple-300' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingItemId ? 'Edit Item' : duplicatingFromItem ? 'Duplicate Item' : 'Add Item'}
              </h3>
              {(editingItemId || duplicatingFromItem) && (
                <button
                  onClick={() => {
                    setEditingItemId(null);
                    setDuplicatingFromItem(null);
                    setFormData(initialFormData);
                    setStorageType('ground');
                    setSelectedZoneId('');
                    setGroundPlacements([]);
                    setRackPlacements([]);
                    setPalletMode('own');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {editingItemId ? 'Cancel Edit' : 'Cancel Duplicate'}
                </button>
              )}
            </div>

            {/* Duplicate mode banner */}
            {duplicatingFromItem && (
              <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl">
                <div className="flex-shrink-0 mt-0.5 w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-900">
                    Duplicating: {duplicatingFromItem.productDescription}
                  </p>
                  <p className="text-xs text-purple-700 mt-0.5">
                    All fields have been copied — edit what you need and click <span className="font-semibold">Add Item</span> to save as a new entry.
                    {duplicatingFromItem.batchNo && (
                      <span className="ml-1">· Batch: <span className="font-mono">{duplicatingFromItem.batchNo}</span></span>
                    )}
                  </p>
                </div>
              </div>
            )}
            
            {/* Section: Product Details */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 bg-brand-500 rounded-full"></div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Product Details</span>
            </div>

            {/* Product Description — searchable from product_master by client */}
            <div className="mb-4 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Description <span className="text-red-500">*</span>
              </label>
              {formData.productMasterId ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-brand-400 rounded-lg bg-brand-50">
                  <span className="flex-1 text-sm font-medium text-brand-900">{formData.productDescription}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(f => ({ ...f, productMasterId: null, productDescription: '', itemCode: '', unitType: '', weightPerItem: '', grossWeightPerItem: '', batchNo: '', skuCode: '', customerSapCode: '', mfgDate: '', expiryDate: '', lotNo: '' }));
                      setProductSearch('');
                      setBatchList([]);
                    }}
                    className="text-brand-500 hover:text-brand-700 text-xs font-semibold"
                  >✕ Change</button>
                </div>
              ) : (
                <div>
                  <input
                    ref={productSearchRef}
                    type="text"
                    value={productSearch}
                    onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                    onFocus={() => setShowProductDropdown(true)}
                    onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                    placeholder={productMasterList.length === 0 ? 'No products found for this client' : 'Search product description…'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    autoComplete="off"
                  />
                  {showProductDropdown && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {productMasterList
                        .filter(p => !productSearch || p.productDescription.toLowerCase().includes(productSearch.toLowerCase()))
                        .slice(0, 50)
                        .map((p: any) => (
                          <button
                            key={p.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b border-gray-100 last:border-0"
                            onMouseDown={() => {
                              setFormData(f => ({
                                ...f,
                                productMasterId: p.id,
                                productDescription: p.productDescription,
                                itemCode: p.itemCode ?? '',
                                unitType: p.unitType ?? 'box',
                                weightPerItem: p.weightPerUnit ?? '',
                                grossWeightPerItem: p.grossWeight ?? '',
                                batchNo: '',
                                skuCode: '',
                                customerSapCode: '',
                                mfgDate: '',
                                expiryDate: '',
                              }));
                              setProductSearch('');
                              setShowProductDropdown(false);
                              loadBatchesForProduct(p.id);
                            }}
                          >
                            <span className="font-medium">{p.productDescription}</span>
                            {p.itemCode && <span className="ml-2 text-xs text-gray-400">{p.itemCode}</span>}
                          </button>
                        ))}
                      {productMasterList.filter(p => !productSearch || p.productDescription.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                        <div className="px-3 py-3 text-sm text-gray-400 text-center">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Item Code — auto-filled from product master */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Code
                  {formData.productMasterId && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                <input
                  type="text"
                  value={formData.itemCode}
                  readOnly={!!formData.productMasterId}
                  onChange={(e) => handleInputChange('itemCode', e.target.value)}
                  placeholder="Auto-filled from product"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent ${formData.productMasterId ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-default' : 'border-gray-300'}`}
                />
              </div>

              {/* Unit Type — auto-filled from product master */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit <span className="text-red-500">*</span>
                  {formData.productMasterId && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                {formData.productMasterId ? (
                  <input
                    type="text"
                    value={formData.unitType}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 border-gray-200 text-gray-600 cursor-default"
                  />
                ) : (
                  <select
                    value={formData.unitType}
                    onChange={(e) => handleInputChange('unitType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">Select unit…</option>
                    {uomList.map((u) => (
                      <option key={u.id} value={u.code}>{u.code}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Weight Per Unit — auto-filled from product master */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight Per Unit (kg)
                  {formData.productMasterId && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  value={formData.weightPerItem}
                  readOnly={!!formData.productMasterId}
                  onChange={(e) => handleInputChange('weightPerItem', sanitizeDecimalQty(e.target.value))}
                  placeholder="Auto-filled from product"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${formData.productMasterId ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-default' : 'border-gray-300'}`}
                />
              </div>

              {/* Batch No — dropdown loaded from product_master batches */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Batch No <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.batchNo}
                  onChange={e => {
                    const selected = batchList.find((b: any) => b.batchNo === e.target.value);
                    setFormData(f => ({
                      ...f,
                      batchNo: e.target.value,
                      skuCode: selected?.skuCode ?? f.skuCode,
                      customerSapCode: selected?.customerSapCode ?? f.customerSapCode,
                      mfgDate: selected?.mfgDate ? selected.mfgDate.slice(0, 16) : '',
                      expiryDate: selected?.expiryDate ? selected.expiryDate.slice(0, 16) : '',
                      lotNo: selected?.lotNo || 'NA',
                    }));
                  }}
                  disabled={!formData.productMasterId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!formData.productMasterId ? 'Select product first' : batchList.length === 0 ? 'No batches available' : 'Select batch…'}
                  </option>
                  {batchList.map((b: any) => (
                    <option key={b.id} value={b.batchNo}>{b.batchNo}</option>
                  ))}
                </select>
              </div>

              {/* SKU Code — locked once the selected batch already carries one */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU Code
                  {selectedBatch?.skuCode && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                <input
                  type="text"
                  value={formData.skuCode}
                  readOnly={!!selectedBatch?.skuCode}
                  onChange={(e) => handleInputChange('skuCode', e.target.value)}
                  placeholder="Enter SKU code"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${selectedBatch?.skuCode ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-default' : 'border-gray-300'}`}
                />
              </div>

              {/* Customer SAP Code — locked once the selected batch already carries one */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer SAP Code
                  {selectedBatch?.customerSapCode && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                <input
                  type="text"
                  value={formData.customerSapCode}
                  readOnly={!!selectedBatch?.customerSapCode}
                  onChange={(e) => handleInputChange('customerSapCode', e.target.value)}
                  placeholder="Enter SAP code"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${selectedBatch?.customerSapCode ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-default' : 'border-gray-300'}`}
                />
              </div>
            </div>

            {/* Section: Dates & Quantities */}
            <div className="flex items-center gap-2 mb-3 pt-2 border-t border-gray-100">
              <div className="w-1 h-4 bg-green-500 rounded-full"></div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates & Quantities</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* MFG DateTime — auto-filled from batch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MFG DateTime
                  {formData.batchNo && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                {formData.batchNo ? (
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">
                    {formData.mfgDate ? formData.mfgDate.replace('T', ' ') : 'NA'}
                  </div>
                ) : (
                  <input
                    type="datetime-local"
                    value={formData.mfgDate}
                    onChange={(e) => handleInputChange('mfgDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                )}
              </div>

              {/* Expiry DateTime — auto-filled from batch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry DateTime
                  {formData.batchNo && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                {formData.batchNo ? (
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">
                    {formData.expiryDate ? formData.expiryDate.replace('T', ' ') : 'NA'}
                  </div>
                ) : (
                  <input
                    type="datetime-local"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    disabled={!formData.mfgDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                )}
              </div>


              {/* Expected Qty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Qty
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.expectedQty}
                  onChange={(e) => handleInputChange('expectedQty', sanitizeIntegerQty(e.target.value))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>


            {/* Quantities */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Good Qty <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.goodQty}
                  onChange={(e) => handleInputChange('goodQty', sanitizeIntegerQty(e.target.value))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  C.DAM Qty
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.cdamQty}
                  onChange={(e) => handleInputChange('cdamQty', sanitizeIntegerQty(e.target.value))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DAM Qty
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.damQty}
                  onChange={(e) => handleInputChange('damQty', sanitizeIntegerQty(e.target.value))}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actual Qty
                </label>
                <input
                  type="text"
                  value={actualQty}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Excess/Short
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={excessQty}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold"
                  />
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${excessStatus.color}`}>
                    {excessStatus.label}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Net Total Weight (kg)
                </label>
                <input
                  type="text"
                  value={totalWeight}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gross Total Weight (kg)
                </label>
                <input
                  type="text"
                  value={grossTotalWeight}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot No <span className="text-gray-400 text-xs font-normal">(optional)</span>
                  {selectedBatch?.lotNo && <span className="ml-1 text-xs text-brand-500 font-normal">auto-filled</span>}
                </label>
                <input
                  type="text"
                  value={formData.lotNo}
                  readOnly={!!selectedBatch?.lotNo}
                  onChange={e => setFormData(f => ({ ...f, lotNo: INPUT_SANITIZERS.lotNoFormat(e.target.value) }))}
                  maxLength={20}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${selectedBatch?.lotNo ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-default' : 'border-gray-300'}`}
                  placeholder="e.g. LOT-2024-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Handling Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.handlingType}
                  onChange={e => setFormData(f => ({ ...f, handlingType: e.target.value as 'manual' | 'mhe' | '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">— Select —</option>
                  <option value="manual">Manual</option>
                  <option value="mhe">MHE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Required Pallets
                </label>
                <input
                  type="text"
                  readOnly
                  value={formData.requiredPalletCount}
                  placeholder="Auto-calculated"
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-600 cursor-not-allowed"
                />
                {selectedBatchQtyPerPallet && goodQtyNumber > 0 && (
                  <p className="text-xs text-brand-600 mt-1">
                    Auto: {Math.ceil(goodQtyNumber / selectedBatchQtyPerPallet)} pallet{Math.ceil(goodQtyNumber / selectedBatchQtyPerPallet) !== 1 ? 's' : ''} based on qty/pallet (Good Qty only)
                  </p>
                )}
              </div>
            </div>

            {selectedBatchQtyPerPallet && goodQtyNumber > 0 && goodQtyNumber % selectedBatchQtyPerPallet !== 0 && (
              <div className="mb-4 flex items-start gap-2 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3">
                <span className="text-yellow-500 mt-0.5">⚠</span>
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Partial pallet:</span>{' '}
                  {goodQtyNumber % selectedBatchQtyPerPallet}/{selectedBatchQtyPerPallet} units filled.{' '}
                  <span className="font-semibold">{selectedBatchQtyPerPallet - (goodQtyNumber % selectedBatchQtyPerPallet)} slots free</span> on last pallet
                  {' '}— consider using <span className="font-semibold">Shared Pallet mode</span> to co-load another product.
                </p>
              </div>
            )}

            {/* Remarks */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                rows={2}
                value={formData.remarks}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow alphanumeric, spaces, commas, periods, hyphens, newlines - max 200 chars
                  const sanitized = value.replace(/[^a-zA-Z0-9\s,.\-\n]/g, '');
                  if (sanitized.length <= 200) {
                    handleInputChange('remarks', sanitized);
                  }
                }}
                placeholder="Enter any remarks (max 200 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                maxLength={200}
              />
              <div className="flex justify-end mt-1">
                <p className={`text-xs font-medium ${formData.remarks.length >= 200 ? 'text-red-600' : 'text-gray-500'}`}>
                  {formData.remarks.length}/200
                </p>
              </div>
            </div>
            {/* Items List — moved to card grid below */}
            {false && (
              <div className="mb-4 mt-2">
                <div className="flex items-center gap-2 mb-3 pt-2 border-t border-gray-100">
                  <div className="w-1 h-4 bg-brand-500 rounded-full"></div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Items List ({items.length})</span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                      <tr>
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={items.length > 0 && selectedForPalletIds.length === items.length}
                            onChange={(e) => setSelectedForPalletIds(e.target.checked ? items.map((i: any) => i.id) : [])}
                            className="w-4 h-4 text-brand-600 rounded border-gray-300"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">SKU Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Item Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Unit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Expected</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Good</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">C.DAM</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">DAM</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actual</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Excess/Short</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Pallets</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Req. / % Free</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Weight</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Expiry</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item: any) => {
                        const itemActualQty = item.goodQty + item.cdamQty + item.damQty;
                        const itemExcess = item.shortExcess || 0;
                        const itemExcessStatus = itemExcess > 0 ? { label: 'Excess', color: 'text-orange-600 bg-orange-100' } :
                                                itemExcess < 0 ? { label: 'Short', color: 'text-red-600 bg-red-100' } :
                                                { label: 'Match', color: 'text-green-600 bg-green-100' };
                        return (
                          <tr key={item.id} className={`hover:bg-brand-50 transition-colors duration-150 ${selectedForPalletIds.includes(item.id) ? 'bg-brand-50' : ''}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedForPalletIds.includes(item.id)}
                                onChange={() => setSelectedForPalletIds(prev =>
                                  prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                                )}
                                className="w-4 h-4 text-brand-600 rounded border-gray-300"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.productDescription}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.skuCode || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.itemCode || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.productCategoryName || item.productCategory?.name || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.unitType}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{itemActualQty - itemExcess}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.goodQty}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.cdamQty}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.damQty}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{itemActualQty}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold">{itemExcess}</span>
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${itemExcessStatus.color}`}>
                                  {itemExcessStatus.label}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.palletCount || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              {item.qtyPerPallet ? (() => {
                                // Pallet planning is Good Qty only — CDAM/DAM pallets are assigned manually.
                                const req = Math.ceil(item.goodQty / item.qtyPerPallet);
                                const remainder = item.goodQty % item.qtyPerPallet;
                                const freeSlots = remainder === 0 ? 0 : item.qtyPerPallet - remainder;
                                const freePct = remainder === 0 ? 0 : Math.round(freeSlots / item.qtyPerPallet * 100);
                                return (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="font-semibold text-brand-700">{req} pallet{req !== 1 ? 's' : ''}</span>
                                    {freePct > 0 ? (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 w-fit">{freePct}% free · {freeSlots} units free</span>
                                    ) : (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 w-fit">Full</span>
                                    )}
                                  </div>
                                );
                              })() : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.totalWeight} kg</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleDuplicateItem(item)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-medium rounded-md hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-sm"
                                  title="Duplicate this item"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Duplicate
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleEditItem(item)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-brand-500 to-brand-600 text-white text-xs font-medium rounded-md hover:from-brand-600 hover:to-brand-700 transition-all duration-200 shadow-sm"
                                  title="Edit item"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-medium rounded-md hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm"
                                  title="Delete item"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Shared Pallet Assignment — moved to card grid below */}
            {false && (
              <div className="mb-4 mt-2 border-2 border-dashed border-brand-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-brand-50 border-b border-brand-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-brand-100 rounded-lg">
                      <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Shared Pallet Assignment</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedForPalletIds.length > 0
                          ? `${selectedForPalletIds.length} product${selectedForPalletIds.length > 1 ? 's' : ''} selected — assign to a shared pallet below`
                          : 'Tick products above to assign multiple products to one pallet'}
                      </p>
                    </div>
                  </div>
                  {selectedForPalletIds.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newGroup = {
                          id: Math.random().toString(36).slice(2, 9),
                          placementType: 'zone' as const,
                          zoneId: '' as number | '',
                          rackId: null as number | null,
                          locationId: null as number | null,
                          itemIds: [...selectedForPalletIds],
                        };
                        setSharedPalletGroups(prev => [...prev, newGroup]);
                        setSelectedForPalletIds([]);
                      }}
                      className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Assign to Shared Pallet
                    </button>
                  )}
                </div>
                <div className="p-4">
                  {selectedForPalletIds.length === 0 && sharedPalletGroups.length === 0 && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 rounded-lg border border-brand-100 text-sm text-brand-700">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Use the checkboxes in the items table above to select 2 or more products, then click <span className="font-semibold mx-1">"Assign to Shared Pallet"</span> to group them on one pallet with a shared zone/rack.
                    </div>
                  )}
                  {selectedForPalletIds.length === 1 && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-yellow-700">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Select at least 2 products to create a shared pallet group.
                    </div>
                  )}
                  {sharedPalletGroups.length > 0 && (
                    <div className="space-y-4">
                      {sharedPalletGroups.map((group, groupIdx) => {
                        const groupItems = items.filter((item: any) => group.itemIds.includes(item.id));
                        const groupLocations = group.rackId ? locationsByRackId[group.rackId] || [] : [];
                        const isLoadingGroupLocations = group.rackId ? !!loadingLocationsByRackId[group.rackId] : false;
                        return (
                          <div key={group.id} className="border border-brand-200 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-brand-50 to-brand-50 border-b border-brand-200">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-brand-700 uppercase tracking-wide">Pallet Group {groupIdx + 1}</span>
                                <span className="text-xs text-gray-500">· {groupItems.length} product{groupItems.length !== 1 ? 's' : ''}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setSharedPalletGroups(prev => prev.filter(g => g.id !== group.id))}
                                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Remove group
                              </button>
                            </div>
                            <div className="p-4 space-y-4">
                              <div className="flex flex-wrap gap-2">
                                {groupItems.map((item: any) => (
                                  <div key={item.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs">
                                    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                    <span className="font-medium text-gray-800">{item.productDescription}</span>
                                    {item.batchNo && <span className="text-gray-400">· {item.batchNo}</span>}
                                    <span className="text-gray-500">{item.goodQty + item.cdamQty + item.damQty} {item.unitType}</span>
                                    <button
                                      type="button"
                                      onClick={() => setSharedPalletGroups(prev => prev.map(g =>
                                        g.id === group.id ? { ...g, itemIds: g.itemIds.filter(id => id !== item.id) } : g
                                      ))}
                                      className="ml-1 text-gray-300 hover:text-red-400"
                                      title="Remove from group"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Placement</label>
                                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => setSharedPalletGroups(prev => prev.map(g =>
                                        g.id === group.id ? { ...g, placementType: 'zone', zoneId: '', rackId: null, locationId: null } : g
                                      ))}
                                      className={`flex-1 py-2 text-xs font-medium transition-colors ${group.placementType === 'zone' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >Zone</button>
                                    <button
                                      type="button"
                                      onClick={() => setSharedPalletGroups(prev => prev.map(g =>
                                        g.id === group.id ? { ...g, placementType: 'rack', zoneId: '', rackId: null, locationId: null } : g
                                      ))}
                                      className={`flex-1 py-2 text-xs font-medium border-l border-gray-300 transition-colors ${group.placementType === 'rack' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >Rack</button>
                                  </div>
                                </div>
                                {group.placementType === 'zone' ? (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                                    <select
                                      value={group.zoneId}
                                      onChange={(e) => setSharedPalletGroups(prev => prev.map(g =>
                                        g.id === group.id ? { ...g, zoneId: e.target.value ? parseInt(e.target.value) : '' } : g
                                      ))}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                    >
                                      <option value="">{normalZones.length === 0 ? 'No zones found' : 'Select zone'}</option>
                                      {normalZones.map((zone: any) => {
                                        if (!zone?.id) return null;
                                        return <option key={zone.id} value={zone.id}>{zoneOptionLabel(zone)}</option>;
                                      })}
                                    </select>
                                  </div>
                                ) : (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Rack</label>
                                    <select
                                      value={group.rackId ?? ''}
                                      onChange={(e) => {
                                        const nextRackId = e.target.value ? parseInt(e.target.value) : null;
                                        setSharedPalletGroups(prev => prev.map(g =>
                                          g.id === group.id ? { ...g, rackId: nextRackId, locationId: null } : g
                                        ));
                                        if (nextRackId) void ensureLocationsLoaded(nextRackId);
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                    >
                                      <option value="">{isLoadingRacks ? 'Loading...' : 'Select rack'}</option>
                                      {[...nonLbdRacks].filter((r: any) => r?.id).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))).map((rack: any) => (
                                        <option key={rack.id} value={rack.id}>{rackOptionLabel(rack)}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                {group.placementType === 'rack' && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                                    <select
                                      value={group.locationId ?? ''}
                                      onChange={(e) => setSharedPalletGroups(prev => prev.map(g =>
                                        g.id === group.id ? { ...g, locationId: e.target.value ? parseInt(e.target.value) : null } : g
                                      ))}
                                      disabled={!group.rackId}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    >
                                      <option value="">{!group.rackId ? 'Select rack first' : isLoadingGroupLocations ? 'Loading...' : 'Select location'}</option>
                                      {groupLocations.map((loc: any) => {
                                        if (!loc?.locationId) return null;
                                        const isOccupied = loc.status === 'occupied';
                                        return (
                                          <option key={loc.locationId} value={loc.locationId} disabled={isOccupied}
                                            style={isOccupied ? { color: '#9CA3AF', fontStyle: 'italic' } : {}}>
                                            {loc.locationCode || 'Unknown'}{formatLocationSpaceLabel(loc)}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Storage Assignment moved to the products list below — assign pallet/location per item via the Pallet button after adding product details */}
            {false && (
            <div>
            {/* Section: Storage Assignment */}
            <div className="flex items-center gap-2 mb-3 pt-2 border-t border-gray-100">
              <div className="w-1 h-4 bg-orange-500 rounded-full"></div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Storage Assignment</span>
            </div>

            <div className="mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={storageType}
                    onChange={(e) => {
                      const next = e.target.value as StorageType;
                      setStorageType(next);
                      setSelectedZoneId('');
                      setGroundPlacements([]);
                      setRackPlacements([]);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="ground">Ground</option>
                    <option value="rack">Rack</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mapping
                  </label>
                  <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
                    {storageType === 'ground'
                      ? 'Split pallets across zones. Sum(pallets) must equal pallet count.'
                      : 'Assign one rack location per pallet. Rows must equal pallet count.'}
                  </div>
                </div>
              </div>

              {storageType === 'ground' && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zone
                      </label>
                      <select
                        value={selectedZoneId}
                        onChange={(e) =>
                          setSelectedZoneId(e.target.value ? parseInt(e.target.value) : '')
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <option value="">
                          {normalZones.length === 0 ? 'No zones found' : 'Select zone'}
                        </option>
                        {normalZones.map((zone: any) => {
                          if (!zone?.id) return null;
                          return (
                            <option key={zone.id} value={zone.id}>
                              {zoneOptionLabel(zone)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedZoneId) return;
                          if (
                            groundPlacements.some((row) => row.zoneId === selectedZoneId)
                          ) {
                            return;
                          }
                          setGroundPlacements((prev) => [
                            ...prev,
                            { zoneId: selectedZoneId, pallets: '1', zoneLocationId: null },
                          ]);
                          void ensureZoneLocationsLoaded(selectedZoneId);
                        }}
                        disabled={!selectedZoneId}
                        className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Add Zone
                      </button>
                    </div>
                  </div>

                  {groundPlacements.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                              Zone
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                              Location
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                              Pallets
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {groundPlacements.map((row) => {
                            const zone = zones.find((z: any) => z.id === row.zoneId);
                            const displayName = zone ?
                              (zone.code ? (zone.name ? `${zone.code} - ${zone.name}` : zone.code) : (zone.name || `Zone ${row.zoneId}`)) :
                              `Zone ${row.zoneId}`;
                            const rowZoneLocations = zoneLocationsByZoneId[row.zoneId] || [];

                            return (
                              <tr key={row.zoneId} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {displayName}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {rowZoneLocations.length > 0 ? (
                                    <select
                                      value={row.zoneLocationId ?? ''}
                                      onChange={(e) => {
                                        const zoneLocationId = e.target.value ? parseInt(e.target.value) : null;
                                        setGroundPlacements((prev) =>
                                          prev.map((r) =>
                                            r.zoneId === row.zoneId ? { ...r, zoneLocationId, pallets: zoneLocationId ? '1' : r.pallets } : r,
                                          ),
                                        );
                                      }}
                                      className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                    >
                                      <option value="">No specific slot</option>
                                      {rowZoneLocations.map((loc: any) => (
                                        <option
                                          key={loc.locationId}
                                          value={loc.locationId}
                                          disabled={loc.status === 'occupied' && loc.locationId !== row.zoneLocationId}
                                        >
                                          {loc.locationCode}{formatLocationSpaceLabel(loc)}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span className="text-gray-300 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <input
                                    type="number"
                                    min="1"
                                    value={row.pallets}
                                    disabled={!!row.zoneLocationId}
                                    onChange={(e) => {
                                      const nextValue = e.target.value;
                                      setGroundPlacements((prev) =>
                                        prev.map((r) =>
                                          r.zoneId === row.zoneId ? { ...r, pallets: nextValue } : r,
                                        ),
                                      );
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    className="w-24 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setGroundPlacements((prev) =>
                                        prev.filter((r) => r.zoneId !== row.zoneId),
                                      )
                                    }
                                    className="text-red-600 hover:text-red-800 text-xs"
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      <div className="mt-2 text-sm text-gray-700">
                        Pallets total:{' '}
                        <span className="font-semibold">
                          {groundPlacements.reduce(
                            (sum, row) => sum + (parseInt(row.pallets) || 0),
                            0,
                          )}
                        </span>{' '}
                        / {palletCountNumber || 0}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {storageType === 'rack' && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3">
                  {palletCountNumber <= 0 ? (
                    <p className="text-sm text-gray-600">Enter pallet count to generate rows.</p>
                  ) : (
                    <div className="space-y-2">
                      {rackPlacements.map((row, index) => {
                        const rackId = row.rackId ?? undefined;
                        const locations = rackId ? locationsByRackId[rackId] || [] : [];
                        const isLoadingLocations = rackId
                          ? !!loadingLocationsByRackId[rackId]
                          : false;

                        return (
                          <div
                            key={index}
                            className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
                          >
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Pallet #{index + 1} Rack
                              </label>  
                              <select
                                value={row.rackId ?? ''}
                                onChange={(e) => {
                                  const nextRackId = e.target.value
                                    ? parseInt(e.target.value)
                                    : null;
                                  setRackPlacements((prev) =>
                                    prev.map((r, idx) =>
                                      idx === index
                                        ? { ...r, rackId: nextRackId, locationId: null }
                                        : r,
                                    ),
                                  );
                                  if (nextRackId) {
                                    void ensureLocationsLoaded(nextRackId);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                              >
                                <option value="">
                                  {isLoadingRacks ? 'Loading racks...' : 'Select rack'}
                                </option>
                                {[...nonLbdRacks].filter((r: any) => r?.id).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))).map((rack: any) => (
                                  <option key={rack.id} value={rack.id}>
                                    {rackOptionLabel(rack)}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Location
                              </label>
                              <select
                                value={row.locationId ?? ''}
                                onChange={(e) => {
                                  const nextLocationId = e.target.value
                                    ? parseInt(e.target.value)
                                    : null;
                                  setRackPlacements((prev) =>
                                    prev.map((r, idx) =>
                                      idx === index ? { ...r, locationId: nextLocationId } : r,
                                    ),
                                  );
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                disabled={!row.rackId}
                              >
                                <option value="">
                                  {!row.rackId
                                    ? 'Select rack first'
                                    : isLoadingLocations
                                      ? 'Loading locations...'
                                      : 'Select location'}
                                </option>
                                {locations.map((loc: any) => {
                                  if (!loc?.locationId) return null;
                                  
                                  const isOccupied = loc.status === 'occupied';
                                  const occupiedBy = loc.occupiedBy;
                                  
                                  let displayText = loc.locationCode || 'Unknown Location';
                                  let tooltipText = '';
                                  
                                  if (isOccupied && occupiedBy) {
                                    displayText += ' 🔒 (Occupied)';
                                    tooltipText = `Occupied by ${occupiedBy.clientName} - ${occupiedBy.productDescription}${occupiedBy.batchNo ? ` - Batch: ${occupiedBy.batchNo}` : ''}`;
                                  } else {
                                    displayText += formatLocationSpaceLabel(loc);
                                  }

                                  return (
                                    <option 
                                      key={loc.locationId} 
                                      value={loc.locationId}
                                      disabled={isOccupied}
                                      title={tooltipText}
                                      style={isOccupied ? { color: '#9CA3AF', fontStyle: 'italic' } : {}}
                                    >
                                      {displayText}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>)}

            {/* Form Action Footer */}
            <div className={`flex items-center justify-between pt-4 mt-2 border-t ${duplicatingFromItem ? 'border-purple-200' : 'border-gray-200'}`}>
              <p className="text-xs text-gray-400">
                {editingItemId && 'Editing saved item — changes will update the existing entry.'}
                {duplicatingFromItem && !editingItemId && (
                  <span className="text-purple-600 font-medium">Creating a new entry copied from {duplicatingFromItem.productDescription}</span>
                )}
                {!editingItemId && !duplicatingFromItem && 'Fill in the details above and click Add Item to save.'}
              </p>
              <button
                onClick={handleAddItem}
                disabled={addItemMutation.isPending || updateItemMutation.isPending}
                className={`px-6 py-2.5 text-white text-sm font-semibold rounded-xl flex items-center gap-2 shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md active:scale-95 ${
                  editingItemId
                    ? 'bg-brand-600 hover:bg-brand-700'
                    : duplicatingFromItem
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {(addItemMutation.isPending || updateItemMutation.isPending) ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {editingItemId ? 'Updating...' : duplicatingFromItem ? 'Saving Duplicate...' : 'Adding...'}
                  </>
                ) : (
                  <>
                    {editingItemId ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : duplicatingFromItem ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                    {editingItemId ? 'Update Item' : duplicatingFromItem ? 'Save as New Item' : 'Add Item'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}


        {/* Product Card Grid */}
        {items.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Products ({items.length})</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(isDraft || isRejected)
                    ? (SHOW_PALLET_SELECTION_CHECKBOXES
                      ? 'Select 2+ products to assign a shared pallet, or use Assign Pallet on individual cards'
                      : 'Use Assign Pallet on individual cards')
                    : 'Products in this GRN'}
                </p>
              </div>
            </div>

            {/* Shared Pallet Assignment Bar */}
            {(isDraft || isRejected) && selectedForPalletIds.length >= 2 && (
              <div className="mb-4 bg-purple-50 border-2 border-purple-200 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-purple-900">{selectedForPalletIds.length} products selected for shared pallet</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedForPalletIds([])}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Clear selection
                  </button>
                </div>

                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Storage Type</label>
                    <select
                      value={sharedAssignStorageType}
                      onChange={(e) => {
                        setSharedAssignStorageType(e.target.value as 'ground' | 'rack');
                        setSharedAssignZoneId('');
                        setSharedAssignRackId(null);
                        setSharedAssignLocationId(null);
                      }}
                      className="px-3 py-1.5 border border-purple-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="ground">Ground (Zone)</option>
                      <option value="rack">Rack</option>
                    </select>
                  </div>

                  {sharedAssignStorageType === 'ground' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Zone</label>
                      <select
                        value={sharedAssignZoneId}
                        onChange={(e) => setSharedAssignZoneId(e.target.value ? parseInt(e.target.value) : '')}
                        className="px-3 py-1.5 border border-purple-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select zone</option>
                        {normalZones.map((z: any) => (
                          <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {sharedAssignStorageType === 'rack' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Rack</label>
                        <select
                          value={sharedAssignRackId ?? ''}
                          onChange={(e) => {
                            const rackId = e.target.value ? parseInt(e.target.value) : null;
                            setSharedAssignRackId(rackId);
                            setSharedAssignLocationId(null);
                            if (rackId) void ensureLocationsLoaded(rackId);
                          }}
                          className="px-3 py-1.5 border border-purple-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select rack</option>
                          {[...nonLbdRacks].filter((r: any) => r?.id).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))).map((rack: any) => (
                            <option key={rack.id} value={rack.id}>{rackOptionLabel(rack)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                        <select
                          value={sharedAssignLocationId ?? ''}
                          onChange={(e) => setSharedAssignLocationId(e.target.value ? parseInt(e.target.value) : null)}
                          disabled={!sharedAssignRackId}
                          className="px-3 py-1.5 border border-purple-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                          <option value="">{!sharedAssignRackId ? 'Select rack first' : 'Select location'}</option>
                          {sharedAssignRackId && (locationsByRackId[sharedAssignRackId] || []).map((loc: any) => loc?.locationId && (
                            <option key={loc.locationId} value={loc.locationId} disabled={loc.status === 'occupied' || loc.isHeld}>
                              {loc.locationCode}{formatLocationSpaceLabel(loc)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={handleAssignSharedPallet}
                    disabled={updateItemMutation.isPending}
                    className="px-5 py-1.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {updateItemMutation.isPending ? 'Saving...' : 'Assign Shared Pallet'}
                  </button>
                </div>
              </div>
            )}

            <div className="grn-items-card-table overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80 border-b border-gray-200">
                  <tr>
                    {(isDraft || isRejected) && SHOW_PALLET_SELECTION_CHECKBOXES && (
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={items.length > 0 && selectedForPalletIds.length === items.length}
                          onChange={(e) => setSelectedForPalletIds(e.target.checked ? items.map((i: any) => i.id) : [])}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 cursor-pointer"
                          title="Select all"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Unit</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-green-700 uppercase tracking-wider">Good</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-yellow-700 uppercase tracking-wider">C.DAM</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-red-700 uppercase tracking-wider">DAM</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-brand-700 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Excess/Short</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Weight</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Expiry</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Pallets</th>
                    {(isDraft || isRejected) && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
              {items.map((item: any) => {
                const itemActualQty = item.goodQty + item.cdamQty + item.damQty;
                const itemExcess = item.shortExcess || 0;
                const isAssigning = assigningPalletForItemId === item.id;
                const isPalletAssigned = item.palletCount > 0 || (Array.isArray(item.storageDetails) && item.storageDetails.length > 0);
                const palletStatus =
                  item.palletCount > 0 ? 'single' :
                  item.palletCount === 0 && Array.isArray(item.storageDetails) && item.storageDetails.length > 0 ? 'shared' :
                  'none';
                const locationLabel = formatStorageLocations(item.storageDetails);
                return (
                  <Fragment key={item.id}>
                    {/* Data row */}
                    <tr className={`grn-row hover:bg-gray-50 transition-colors ${selectedForPalletIds.includes(item.id) ? 'bg-purple-50' : ''}`}>
                      {(isDraft || isRejected) && SHOW_PALLET_SELECTION_CHECKBOXES && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedForPalletIds.includes(item.id)}
                            onChange={() => setSelectedForPalletIds((prev) =>
                              prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
                            )}
                            className="w-4 h-4 text-purple-600 rounded border-gray-300 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="grn-cell-title px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{item.productDescription}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {item.batchNo && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">Batch: {item.batchNo}</span>}
                          {item.skuCode && <span className="text-xs bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded font-mono">SKU: {item.skuCode}</span>}
                        </div>
                      </td>
                      <td data-label="Unit" className="px-4 py-3 text-sm text-gray-600">{item.unitType}</td>
                      <td data-label="Good" className="px-4 py-3.5 text-center text-sm font-semibold text-green-600">{item.goodQty}</td>
                      <td data-label="C.DAM" className="px-4 py-3.5 text-center text-sm font-semibold text-amber-600">{item.cdamQty}</td>
                      <td data-label="DAM" className="px-4 py-3.5 text-center text-sm font-semibold text-red-500">{item.damQty}</td>
                      <td data-label="Total" className="px-4 py-3.5 text-center"><span className="inline-flex items-center justify-center min-w-[2rem] text-sm font-bold text-brand-700 bg-brand-50 px-2 py-0.5 rounded-md">{itemActualQty}</span></td>
                      <td data-label="Excess/Short" className="px-4 py-3 text-sm">
                        {itemExcess === 0 ? (
                          <span className="text-green-600 font-semibold">Match</span>
                        ) : (
                          <span className={`font-semibold ${itemExcess > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                            {itemExcess > 0 ? `+${itemExcess}` : itemExcess}
                          </span>
                        )}
                      </td>
                      <td data-label="Weight" className="px-4 py-3 text-sm text-gray-600">{item.totalWeight > 0 ? `${item.totalWeight} kg` : '-'}</td>
                      <td data-label="Expiry" className="px-4 py-3 text-sm text-gray-600">{item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}</td>
                      <td data-label="Pallets" className="grn-cell-stack px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`w-fit text-xs font-semibold px-2 py-1 rounded-full ${
                            palletStatus === 'single' ? 'bg-brand-100 text-brand-700' :
                            palletStatus === 'shared' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {palletStatus === 'single' ? `${item.palletCount} Pallet${item.palletCount !== 1 ? 's' : ''}` :
                             palletStatus === 'shared' ? 'Shared Pallet' : 'Not Assigned'}
                          </span>
                          {item.qtyPerPallet && item.goodQty > 0 && (() => {
                            // Pallet planning is Good Qty only — CDAM/DAM pallets are assigned manually.
                            const req = Math.ceil(item.goodQty / item.qtyPerPallet);
                            const filledOnLast = item.goodQty % item.qtyPerPallet;
                            const isExactFull = filledOnLast === 0;
                            const freeOnLast = isExactFull ? 0 : item.qtyPerPallet - filledOnLast;
                            const freePct = isExactFull ? 0 : Math.round(freeOnLast / item.qtyPerPallet * 100);
                            const freeSqFt = item.isOversized && item.palletSizeSqft
                              ? Math.round(item.palletSizeSqft * freePct / 100 * 10) / 10
                              : null;
                            return (
                              <div className="flex flex-col gap-0.5 mt-0.5 border-t border-gray-100 pt-1">
                                <span className="text-xs font-bold text-brand-700">{req} pallet{req !== 1 ? 's' : ''} required</span>
                                {isExactFull ? (
                                  <span className="text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded w-fit">All pallets full</span>
                                ) : (
                                  <>
                                    <span className="text-xs text-gray-500">
                                      Last pallet: <strong>{filledOnLast}/{item.qtyPerPallet}</strong> filled
                                    </span>
                                    <span className="text-xs font-semibold text-yellow-700 bg-yellow-50 px-1.5 py-0.5 rounded w-fit">
                                      {freePct}% free{freeSqFt !== null ? ` — ${freeSqFt} sq ft avail.` : ` — ${freeOnLast} unit${freeOnLast !== 1 ? 's' : ''} free`}
                                    </span>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                          {locationLabel && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600" title={`Stored at ${locationLabel}`}>
                              <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="font-medium">{locationLabel}</span>
                            </span>
                          )}
                          {item.lbdStorageMode && (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600" title="Damaged stock stored separately in LBD zone">
                              <span className="w-fit text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">LBD</span>
                              <span className="font-medium">{getLbdZoneLabel(item) || 'assigned'}</span>
                            </span>
                          )}
                          {item.isOversized && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700" title={`Oversized pallet — ${item.palletSizeSqft} sqft, stored in Heavy zone`}>
                              <span className="w-fit text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">OVERSIZED</span>
                              <span className="font-medium">{item.palletSizeSqft} sqft</span>
                            </span>
                          )}
                        </div>
                      </td>
                      {(isDraft || isRejected) && (
                        <td className="grn-cell-actions px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-nowrap">
                            <button
                              type="button"
                              onClick={() => handleDuplicateItem(item)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg hover:bg-purple-100 transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              Dup
                            </button>
                            <button
                              type="button"
                              onClick={() => { handleEditItem(item); itemFormRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-50 text-brand-700 text-xs font-medium rounded-lg hover:bg-brand-100 transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteItem(item.id)}
                              disabled={isPalletAssigned && !isReopenedForEdit}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${isPalletAssigned && !isReopenedForEdit ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              Del
                            </button>
                            <button
                              type="button"
                              disabled={isPalletAssigned && !isAssigning && !isReopenedForEdit}
                              onClick={() => {
                                setAssigningPalletForItemId(isAssigning ? null : item.id);
                                if (!isAssigning) {
                                  setLocationsByRackId({});
                                  const details = Array.isArray(item.storageDetails) ? item.storageDetails : [];
                                  const isShared = item.palletCount === 0 && details.length > 0;
                                  const storageType = item.storageType || 'ground';
                                  setCardPalletMode(isShared ? 'shared' : 'own');
                                  setCardPalletCount(item.requiredPalletCount > 0 ? String(item.requiredPalletCount) : (item.palletCount > 0 ? String(item.palletCount) : ''));
                                  setCardStorageType(storageType);
                                  // Pre-fill the LBD (damaged) storage panel from the item.
                                  const lbdDetails = Array.isArray(item.lbdStorageDetails) ? item.lbdStorageDetails : [];
                                  const lbdIsRackBased = lbdDetails.length > 0 && lbdDetails.every((d: any) => d.rackId != null);
                                  setCardLbdMode(item.lbdStorageMode || 'dedicated');
                                  setCardLbdPlacementType(lbdIsRackBased ? 'rack' : 'zone');
                                  setCardLbdZoneId(lbdDetails[0]?.zoneId ?? '');
                                  const damagedQtyForSeed = (item.cdamQty || 0) + (item.damQty || 0);
                                  const estimatedLbdPalletCount = item.qtyPerPallet && item.qtyPerPallet > 0
                                    ? Math.ceil(damagedQtyForSeed / item.qtyPerPallet)
                                    : (damagedQtyForSeed > 0 ? 1 : 0);
                                  setCardLbdPlacements(
                                    lbdDetails.length
                                      ? lbdDetails.map((d: any) => (
                                          lbdIsRackBased
                                            ? { zoneId: '', pallets: '1', zoneLocationId: null, rackId: d.rackId ?? null, locationId: d.locationId ?? null }
                                            : { zoneId: d.zoneId ?? '', pallets: String(d.pallets ?? 1), zoneLocationId: d.zoneLocationId ?? null }
                                        ))
                                      : Array.from({ length: estimatedLbdPalletCount > 0 ? estimatedLbdPalletCount : 1 }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null })),
                                  );
                                  setCardLbdRemark(item.lbdRemark || '');
                                  setCardLbdSharedPalletRef(item.lbdSharedPalletRef || '');
                                  lbdDetails.forEach((d: any) => {
                                    if (d.zoneId) void ensureZoneLocationsLoaded(d.zoneId);
                                    if (d.rackId) void ensureLocationsLoaded(d.rackId);
                                  });
                                  setCardIsOversized(!!item.isOversized);
                                  if (storageType === 'ground') {
                                    setCardGroundPlacements(
                                      details.length
                                        ? details.map((d: any) => ({ zoneId: d.zoneId, pallets: String(d.pallets ?? 0), zoneLocationId: d.zoneLocationId ?? null }))
                                        : (isShared
                                            ? [{ zoneId: '', pallets: '0' }]
                                            // Nothing to seed until the pallet count is actually known — an
                                            // unspecified required/manual count means 0 rows, not a phantom "1".
                                            : Array.from({ length: item.requiredPalletCount > 0 ? item.requiredPalletCount : (item.palletCount > 0 ? item.palletCount : 0) }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null }))),
                                    );
                                    setCardRackPlacements([]);
                                    details.forEach((d: any) => { if (d.zoneId) void ensureZoneLocationsLoaded(d.zoneId); });
                                  } else {
                                    setCardRackPlacements(
                                      details.length
                                        ? details.map((d: any) => ({ rackId: d.rackId, locationId: d.locationId }))
                                        // Nothing to seed until the pallet count is actually known — an
                                        // unspecified required/manual count means 0 rows, not a phantom "1".
                                        : Array.from({ length: item.requiredPalletCount > 0 ? item.requiredPalletCount : (item.palletCount > 0 ? item.palletCount : 0) }, () => ({ rackId: null, locationId: null })),
                                    );
                                    setCardGroundPlacements([]);
                                    details.forEach((d: any) => { if (d.rackId) void ensureLocationsLoaded(d.rackId); });
                                  }
                                }
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${isAssigning ? 'bg-brand-600 text-white' : (isPalletAssigned && !isReopenedForEdit) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                              {isAssigning ? 'Close' : 'Pallet'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>

                    {/* Expandable pallet assignment row */}
                    {isAssigning && (isDraft || isRejected) && (() => {
                            const requiredPallets = item.requiredPalletCount > 0 ? item.requiredPalletCount : (parseInt(cardPalletCount) || 0);
                            const placedPallets = cardStorageType === 'ground'
                              ? cardGroundPlacements.reduce((s, p) => s + (parseInt(p.pallets) || 0), 0)
                              : cardRackPlacements.filter((p) => p.rackId && p.locationId).length;

                            // Pallet Count is only free-typed when no requiredPalletCount was
                            // fetched from the batch — cap that manual entry at what the
                            // warehouse's zones/racks can actually still hold.
                            const manualPalletCapacity = cardStorageType === 'rack' ? warehouseRackCapacity : warehouseZoneCapacity;
                            const manualPalletCountExceedsCapacity =
                              item.requiredPalletCount <= 0 && (parseInt(cardPalletCount) || 0) > manualPalletCapacity;

                            const palletDetailsFields = (
                              <div className="space-y-3">
                                {/* Oversized / heavy pallet — big pallets are stored in Heavy zones only */}
                                <label className="flex items-center gap-2 w-fit cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={cardIsOversized}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setCardIsOversized(checked);
                                      if (checked) {
                                        setCardStorageType('ground');
                                        setCardRackPlacements([]);
                                        setCardGroundPlacements((prev) => prev.length ? prev : [{ zoneId: '', pallets: '' }]);
                                      }
                                    }}
                                    className="w-4 h-4 text-amber-600 rounded border-gray-300"
                                  />
                                  <span className="text-xs font-semibold text-amber-700">Oversized / Heavy pallet (stored in Heavy zone only)</span>
                                </label>
                                {cardIsOversized && heavyZones.length === 0 && (
                                  <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 w-fit">
                                    No Heavy zone exists. Create a zone with type "heavy" (or a code starting with "HEAVY") in Warehouse Management first.
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Pallet Count{item.requiredPalletCount > 0 ? ` (required: ${item.requiredPalletCount})` : ''}
                                    </label>
                                    <input
                                      type="text" inputMode="numeric" pattern="[0-9]*" value={cardPalletCount}
                                      readOnly={item.requiredPalletCount > 0}
                                      onChange={(e) => {
                                        if (item.requiredPalletCount > 0) return;
                                        const sanitized = INPUT_SANITIZERS.positiveIntegerFormat(e.target.value);
                                        setCardPalletCount(sanitized);
                                        const typedCount = parseInt(sanitized) || 0;
                                        if (cardStorageType === 'rack') {
                                          const rowCount = Math.max(0, Math.min(typedCount, warehouseRackCapacity));
                                          setCardRackPlacements(Array.from({ length: rowCount }, () => ({ rackId: null, locationId: null })));
                                        } else if (cardStorageType === 'ground') {
                                          const rowCount = Math.max(0, Math.min(typedCount, warehouseZoneCapacity));
                                          setCardGroundPlacements(Array.from({ length: rowCount }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null })));
                                        }
                                      }}
                                      className={`w-32 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${item.requiredPalletCount > 0 ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' : manualPalletCountExceedsCapacity ? 'border-red-400 focus:ring-red-500' : 'border-gray-300'}`}
                                      placeholder="e.g. 2"
                                    />
                                    {manualPalletCountExceedsCapacity && (
                                      <p className="mt-1 text-[11px] text-red-600 w-48">
                                        Only {manualPalletCapacity} pallet{manualPalletCapacity !== 1 ? 's' : ''} of {cardStorageType === 'rack' ? 'rack' : 'zone'} space available in this warehouse — reduce the count.
                                      </p>
                                    )}
                                  </div>
                                  <div className="w-full sm:w-auto">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Storage</label>
                                    <PanelSelect
                                      options={[{ value: 'ground', label: 'Ground' }, { value: 'rack', label: 'Rack' }]}
                                      value={cardStorageType}
                                      onChange={(v) => {
                                        const t = v as 'ground' | 'rack';
                                        setCardStorageType(t);
                                        const typedCount = parseInt(cardPalletCount) || 0;
                                        const isManual = item.requiredPalletCount <= 0;
                                        const groundRowCount = isManual ? Math.max(0, Math.min(typedCount, warehouseZoneCapacity)) : typedCount;
                                        const rackRowCount = isManual ? Math.max(0, Math.min(typedCount, warehouseRackCapacity)) : typedCount;
                                        setCardGroundPlacements(t === 'ground' ? Array.from({ length: groundRowCount }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null })) : []);
                                        setCardRackPlacements(t === 'rack' ? Array.from({ length: rackRowCount }, () => ({ rackId: null, locationId: null })) : []);
                                      }}
                                      placeholder="Select storage"
                                      disabled={cardIsOversized}
                                      className="w-full sm:w-40 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            );

                            const placementHint = cardStorageType === 'ground' ? 'which zone does each pallet go to?' : 'pick a rack and location for each pallet';

                            const placementSection = (
                              <>
                                {cardStorageType === 'ground' && (
                                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="overflow-x-hidden lg:overflow-x-auto">
                                      <table className="w-full table-fixed lg:table-auto lg:min-w-[420px] text-xs">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                          <tr>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide w-6 lg:w-10">#</th>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide">{cardIsOversized ? 'Heavy Zone' : 'Zone'}</th>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide">Location</th>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide w-20 lg:w-32">Pallets</th>
                                            <th className="w-9 lg:w-12"></th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {cardGroundPlacements.map((gp, idx) => {
                                            const zoneLocations = gp.zoneId ? (zoneLocationsByZoneId[gp.zoneId as number] || []) : [];
                                            return (
                                            <tr key={idx}>
                                              <td className="px-2 lg:px-3 py-2 text-gray-500 font-medium">{idx + 1}</td>
                                              <td className="px-2 lg:px-3 py-2">
                                                <PanelSelect
                                                  options={[
                                                    { value: '', label: cardIsOversized ? 'Select heavy zone' : 'Select zone' },
                                                    ...(cardIsOversized ? heavyZones : normalZones).map((z: any) => ({ value: String(z.id), label: zoneOptionLabel(z) })),
                                                  ]}
                                                  value={String(gp.zoneId ?? '')}
                                                  onChange={(v) => {
                                                    const zoneId = v ? parseInt(v) : ('' as number | '');
                                                    setCardGroundPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, zoneId, zoneLocationId: null } : r));
                                                    if (zoneId) void ensureZoneLocationsLoaded(zoneId);
                                                  }}
                                                  placeholder={cardIsOversized ? 'Select heavy zone' : 'Select zone'}
                                                  className="w-full lg:max-w-[16rem]"
                                                />
                                              </td>
                                              <td className="px-2 lg:px-3 py-2">
                                                {zoneLocations.length > 0 ? (() => {
                                                  // Same FCFS rule as rack locations: every pallet except a genuinely
                                                  // partial last one must go to a completely empty slot. Only when the
                                                  // last pallet doesn't need a full pallet's worth of space do we relax
                                                  // to "enough free % remains", so a partially-filled slot can be reused
                                                  // instead of wasting a fresh empty one on a small leftover quantity.
                                                  const isLastPallet = idx === cardGroundPlacements.length - 1;
                                                  const qtyPerPallet = item.qtyPerPallet;
                                                  // Pallet planning is Good Qty only — CDAM/DAM pallets are assigned manually.
                                                  const remainder = qtyPerPallet && qtyPerPallet > 0 ? item.goodQty % qtyPerPallet : 0;
                                                  const isPartialLastPallet = isLastPallet && !!qtyPerPallet && qtyPerPallet > 0 && remainder > 0;
                                                  const requiredFreePct = isPartialLastPallet ? (remainder / qtyPerPallet) * 100 : 100;
                                                  const CAPACITY_TOLERANCE_PCT = 0.5;
                                                  return (
                                                    <>
                                                      <PanelSelect
                                                        options={[
                                                          { value: '', label: 'No specific slot' },
                                                          ...zoneLocations.map((loc: any) => {
                                                            const freePct = 100 - (loc.occupiedPercentage ?? 0);
                                                            // A slot already picked on another row of this same table
                                                            // can't be picked again — matches the backend's "Zone
                                                            // location selections cannot be duplicated" rule, but
                                                            // catches it in the dropdown instead of on Save.
                                                            const pickedOnAnotherRow = cardGroundPlacements.some(
                                                              (other, i) => i !== idx && other.zoneLocationId === loc.locationId,
                                                            );
                                                            return {
                                                              value: String(loc.locationId),
                                                              label: `${loc.locationCode}${formatLocationSpaceLabel(loc)}`,
                                                              disabled: loc.locationId !== gp.zoneLocationId && (pickedOnAnotherRow || loc.isHeld || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT),
                                                            };
                                                          }),
                                                        ]}
                                                        value={String(gp.zoneLocationId ?? '')}
                                                        onChange={(v) => {
                                                          const zoneLocationId = v ? parseInt(v) : null;
                                                          setCardGroundPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, zoneLocationId, pallets: zoneLocationId ? '1' : r.pallets } : r));
                                                        }}
                                                        placeholder="No specific slot"
                                                        className="w-full lg:max-w-[14rem]"
                                                      />
                                                      {isPartialLastPallet && (
                                                        <p className="mt-1 text-[11px] text-accent-600">Partial last pallet — slots with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                                                      )}
                                                    </>
                                                  );
                                                })() : (
                                                  <span className="text-gray-300 text-xs">—</span>
                                                )}
                                              </td>
                                              <td className="px-2 lg:px-3 py-2">
                                                {gp.zoneLocationId ? (
                                                  <span className="text-xs text-gray-500 font-medium">1</span>
                                                ) : (
                                                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={gp.pallets} onChange={(e) => setCardGroundPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, pallets: INPUT_SANITIZERS.positiveIntegerFormat(e.target.value) } : r))} className="w-16 lg:w-24 px-2 lg:px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500" placeholder="Qty" />
                                                )}
                                              </td>
                                              <td className="px-1 lg:px-3 py-2 text-center">
                                                {(() => {
                                                  // Once every required pallet has a row, the row list is locked —
                                                  // matches "+ Add Zone" hiding at the same threshold below.
                                                  const assigned = cardGroundPlacements.reduce((s, p) => s + (parseInt(p.pallets) || 0), 0);
                                                  const required = item.requiredPalletCount > 0 ? item.requiredPalletCount : (parseInt(cardPalletCount) || 0);
                                                  const isFull = required > 0 && assigned >= required;
                                                  return !isFull ? (
                                                    <button type="button" title="Remove zone" onClick={() => setCardGroundPlacements((prev) => prev.filter((_, i) => i !== idx))} className="w-7 h-7 inline-flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 text-sm transition-colors">✕</button>
                                                  ) : null;
                                                })()}
                                              </td>
                                            </tr>
                                            );
                                          })}
                                          {cardGroundPlacements.length === 0 && (
                                            <tr>
                                              <td colSpan={5} className="px-3 py-5 text-center text-gray-400">No zones added yet — use “Add Zone” below.</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
                                      {(() => {
                                        // Matches the "Placed" sidebar stat — total pallets committed across
                                        // rows, whether or not a zone has been picked for each one yet.
                                        const assigned = cardGroundPlacements.reduce((s, p) => s + (parseInt(p.pallets) || 0), 0);
                                        const required = item.requiredPalletCount > 0 ? item.requiredPalletCount : (parseInt(cardPalletCount) || 0);
                                        const isFull = required > 0 && assigned >= required;
                                        return (
                                          <>
                                            {!isFull && (
                                              <button
                                                type="button"
                                                onClick={() => setCardGroundPlacements((prev) => {
                                                  // Carry over the last row's zone — placing several pallets into
                                                  // specific slots of the same zone is the common case, so only the
                                                  // location should need picking again.
                                                  const lastZoneId = prev.length > 0 ? prev[prev.length - 1].zoneId : '';
                                                  return [...prev, { zoneId: lastZoneId, pallets: '', zoneLocationId: null }];
                                                })}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-dashed border-brand-300 text-brand-600 rounded-lg text-xs font-semibold hover:bg-brand-50 hover:border-brand-400 transition-colors"
                                              >
                                                + Add Zone
                                              </button>
                                            )}
                                            {required > 0 && (
                                              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${assigned === required ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {assigned} / {required} pallets placed
                                              </span>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}

                                {cardStorageType === 'rack' && (
                                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="overflow-x-hidden lg:overflow-x-auto">
                                      <table className="w-full table-fixed lg:table-auto lg:min-w-[480px] text-xs">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                          <tr>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide w-9 lg:w-16">Pallet</th>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide">Rack</th>
                                            <th className="text-left px-2 lg:px-3 py-2 font-semibold text-gray-600 uppercase tracking-wide">Location</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {cardRackPlacements.map((rp, idx) => (
                                            <tr key={idx} className="align-top">
                                              <td className="px-2 lg:px-3 py-2.5 font-semibold text-gray-700">#{idx + 1}</td>
                                              <td className="px-2 lg:px-3 py-2.5">
                                                <PanelSelect
                                                  options={[
                                                    { value: '', label: 'Select rack' },
                                                    ...[...nonLbdRacks].filter((r: any) => r?.id).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))).map((rack: any) => ({ value: String(rack.id), label: rackOptionLabel(rack) })),
                                                  ]}
                                                  value={String(rp.rackId ?? '')}
                                                  onChange={(v) => { const rackId = v ? parseInt(v) : null; setCardRackPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, rackId, locationId: null } : r)); if (rackId) void ensureLocationsLoaded(rackId); }}
                                                  placeholder="Select rack"
                                                  className="w-full lg:max-w-[16rem]"
                                                />
                                              </td>
                                              <td className="px-2 lg:px-3 py-2.5">
                                                {(() => {
                                                  // FCFS: every pallet except a genuinely partial last one must go to a
                                                  // completely empty location. Only when the last pallet doesn't need a
                                                  // full pallet's worth of space do we relax to "enough free % remains",
                                                  // so a partially-occupied location can be reused instead of wasting a
                                                  // fresh empty one on a small leftover quantity.
                                                  const isLastPallet = idx === cardRackPlacements.length - 1;
                                                  const qtyPerPallet = item.qtyPerPallet;
                                                  // Pallet planning is Good Qty only — CDAM/DAM pallets are assigned manually.
                                                  const remainder = qtyPerPallet && qtyPerPallet > 0 ? item.goodQty % qtyPerPallet : 0;
                                                  const isPartialLastPallet = isLastPallet && !!qtyPerPallet && qtyPerPallet > 0 && remainder > 0;
                                                  const requiredFreePct = isPartialLastPallet ? (remainder / qtyPerPallet) * 100 : 100;
                                                  const CAPACITY_TOLERANCE_PCT = 0.5;
                                                  return (
                                                    <>
                                                      <PanelSelect
                                                        options={[
                                                          { value: '', label: !rp.rackId ? 'Select rack first' : 'Select location' },
                                                          ...(rp.rackId ? (locationsByRackId[rp.rackId] || []).filter((loc: any) => loc?.locationId).map((loc: any) => {
                                                            const freePct = 100 - (loc.occupiedPercentage ?? 0);
                                                            // A slot already picked on another row of this same table
                                                            // can't be picked again — matches the backend's "Locations
                                                            // cannot be duplicated" rule, but catches it in the
                                                            // dropdown instead of on Save.
                                                            const pickedOnAnotherRow = cardRackPlacements.some(
                                                              (other, i) => i !== idx && other.locationId === loc.locationId,
                                                            );
                                                            return {
                                                              value: String(loc.locationId),
                                                              label: `${loc.locationCode}${formatLocationSpaceLabel(loc)}`,
                                                              disabled: loc.locationId !== rp.locationId && (pickedOnAnotherRow || loc.isHeld || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT),
                                                            };
                                                          }) : []),
                                                        ]}
                                                        value={String(rp.locationId ?? '')}
                                                        onChange={(v) => { const locationId = v ? parseInt(v) : null; setCardRackPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, locationId } : r)); }}
                                                        disabled={!rp.rackId}
                                                        placeholder={!rp.rackId ? 'Select rack first' : 'Select location'}
                                                        className="w-full lg:max-w-[16rem]"
                                                      />
                                                      {isPartialLastPallet && (
                                                        <p className="mt-1 text-[11px] text-accent-600">Partial last pallet — locations with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                                                      )}
                                                    </>
                                                  );
                                                })()}
                                              </td>
                                            </tr>
                                          ))}
                                          {cardRackPlacements.length === 0 && (
                                            <tr>
                                              <td colSpan={3} className="px-3 py-5 text-center text-gray-400">Enter a pallet count above to add rack rows.</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </>
                            );

                            const sharedSection = cardPalletMode === 'shared' ? (
                              <div className="space-y-3">
                                <p className="text-xs text-purple-700 bg-purple-100 rounded-lg px-3 py-2 w-fit">
                                  This product shares one pallet with others — pick where that shared pallet sits.
                                </p>
                                <div className="w-full sm:w-auto">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Storage</label>
                                  <PanelSelect
                                    options={[{ value: 'ground', label: 'Ground' }, { value: 'rack', label: 'Rack' }]}
                                    value={cardStorageType}
                                    onChange={(v) => {
                                      const t = v as 'ground' | 'rack';
                                      setCardStorageType(t);
                                      setCardGroundPlacements([{ zoneId: '', pallets: '0' }]);
                                      setCardRackPlacements([{ rackId: null, locationId: null }]);
                                    }}
                                    placeholder="Select storage"
                                    className="w-full sm:w-40 text-sm"
                                  />
                                </div>

                                {cardStorageType === 'ground' ? (
                                  <div className="w-full sm:w-auto">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Zone</label>
                                    <PanelSelect
                                      options={[
                                        { value: '', label: 'Select zone' },
                                        ...normalZones.map((z: any) => ({ value: String(z.id), label: zoneOptionLabel(z) })),
                                      ]}
                                      value={String(cardGroundPlacements[0]?.zoneId ?? '')}
                                      onChange={(v) => { const zoneId = v ? parseInt(v) : ('' as number | ''); setCardGroundPlacements([{ zoneId, pallets: '0' }]); }}
                                      placeholder="Select zone"
                                      className="w-full sm:w-56 text-sm"
                                    />
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap gap-3">
                                    <div className="w-full sm:w-auto">
                                      <label className="block text-xs text-gray-500 mb-1">Rack</label>
                                      <PanelSelect
                                        options={[
                                          { value: '', label: 'Select rack' },
                                          ...[...nonLbdRacks].filter((r: any) => r?.id).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))).map((rack: any) => ({ value: String(rack.id), label: rackOptionLabel(rack) })),
                                        ]}
                                        value={String(cardRackPlacements[0]?.rackId ?? '')}
                                        onChange={(v) => { const rackId = v ? parseInt(v) : null; setCardRackPlacements([{ rackId, locationId: null }]); if (rackId) void ensureLocationsLoaded(rackId); }}
                                        placeholder="Select rack"
                                        className="w-full sm:w-44 text-sm"
                                      />
                                    </div>
                                    <div className="w-full sm:w-auto">
                                      <label className="block text-xs text-gray-500 mb-1">Location</label>
                                      <PanelSelect
                                        options={[
                                          { value: '', label: !cardRackPlacements[0]?.rackId ? 'Select rack first' : 'Select location' },
                                          ...(cardRackPlacements[0]?.rackId ? (locationsByRackId[cardRackPlacements[0].rackId] || []).filter((loc: any) => loc?.locationId).map((loc: any) => ({
                                            value: String(loc.locationId),
                                            label: `${loc.locationCode}${formatLocationSpaceLabel(loc)}`,
                                            disabled: loc.locationId !== cardRackPlacements[0]?.locationId && (loc.status === 'occupied' || loc.isHeld),
                                          })) : []),
                                        ]}
                                        value={String(cardRackPlacements[0]?.locationId ?? '')}
                                        onChange={(v) => { const locationId = v ? parseInt(v) : null; setCardRackPlacements((prev) => [{ rackId: prev[0]?.rackId ?? null, locationId }]); }}
                                        disabled={!cardRackPlacements[0]?.rackId}
                                        placeholder={!cardRackPlacements[0]?.rackId ? 'Select rack first' : 'Select location'}
                                        className="w-full sm:w-44 text-sm"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null;

                            // LBD / Damaged storage — damaged & c-damaged stock goes to an LBD zone only
                            let lbdRequiredPallets = 0;
                            const lbdSection = (item.cdamQty + item.damQty) > 0 ? (
                              <div className="pt-4 border-t border-dashed border-red-200">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <p className="text-[11px] font-bold text-red-700 uppercase tracking-widest">3 · Damaged Stock (LBD)</p>
                                  <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-semibold">
                                    {item.cdamQty + item.damQty} damaged unit{(item.cdamQty + item.damQty) !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mb-2">Damaged and c-damaged stock must be stored separately in an LBD zone or LBD rack.</p>
                                {lbdZones.length === 0 && lbdRacks.length === 0 ? (
                                  <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 w-fit">
                                    No LBD zone or rack exists. Create a zone or rack with type "lbd" (or a code starting with "LBD") in Warehouse Management first.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex gap-2 w-56">
                                      <button type="button" onClick={() => setCardLbdMode('dedicated')} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${cardLbdMode === 'dedicated' ? 'bg-red-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>Dedicated</button>
                                    </div>

                                    {cardLbdMode === 'dedicated' && (
                                      <div className="flex gap-2 w-56">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setCardLbdPlacementType('zone');
                                            const rowCount = Math.max(1, cardLbdPlacements.length, lbdRequiredPallets);
                                            setCardLbdPlacements(Array.from({ length: rowCount }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null })));
                                          }}
                                          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${cardLbdPlacementType === 'zone' ? 'bg-gray-800 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                                        >
                                          Zone
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setCardLbdPlacementType('rack');
                                            const rowCount = Math.max(1, cardLbdPlacements.length, lbdRequiredPallets);
                                            setCardLbdPlacements(Array.from({ length: rowCount }, () => ({ zoneId: '', pallets: '1', zoneLocationId: null, rackId: null, locationId: null })));
                                          }}
                                          className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${cardLbdPlacementType === 'rack' ? 'bg-gray-800 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                                        >
                                          Rack
                                        </button>
                                      </div>
                                    )}

                                    {cardLbdMode === 'shared' ? (
                                      <div className="flex gap-2 items-end flex-wrap">
                                        <div className="w-full sm:w-auto">
                                          <label className="block text-xs font-medium text-gray-700 mb-1">LBD Zone</label>
                                          <PanelSelect
                                            options={[
                                              { value: '', label: 'Select LBD zone' },
                                              ...lbdZones.map((z: any) => ({ value: String(z.id), label: zoneOptionLabel(z) })),
                                            ]}
                                            value={String(cardLbdZoneId ?? '')}
                                            onChange={(v) => setCardLbdZoneId(v ? parseInt(v) : '')}
                                            placeholder="Select LBD zone"
                                            className="w-full sm:w-48"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Shared Pallet Ref</label>
                                          <input type="text" value={cardLbdSharedPalletRef} onChange={(e) => setCardLbdSharedPalletRef(e.target.value)} className="w-40 px-2 py-1.5 border border-gray-200 rounded-lg text-xs" placeholder="e.g. LBD-SP-01" />
                                        </div>
                                        <div className="flex-1 min-w-[10rem]">
                                          <label className="block text-xs font-medium text-gray-700 mb-1">LBD Remark</label>
                                          <input type="text" value={cardLbdRemark} onChange={(e) => setCardLbdRemark(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs" placeholder="Optional" />
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {/* Informational only — how many pallets the damaged qty would fill,
                                            same formula as the Good Qty hint above but CDAM+DAM based. LBD
                                            Pallets stays manually controlled (per placement row below). */}
                                        {(() => {
                                          const damaged = item.cdamQty + item.damQty;
                                          lbdRequiredPallets = item.qtyPerPallet ? Math.ceil(damaged / item.qtyPerPallet) : 0;
                                          if (!item.qtyPerPallet) return null;
                                          const remainder = damaged % item.qtyPerPallet;
                                          const freeSlots = remainder === 0 ? 0 : item.qtyPerPallet - remainder;
                                          const freePct = remainder === 0 ? 0 : Math.round(freeSlots / item.qtyPerPallet * 100);
                                          return (
                                            <p className="text-xs text-brand-600">
                                              Est. {lbdRequiredPallets} pallet{lbdRequiredPallets !== 1 ? 's' : ''} for {damaged} damaged unit{damaged !== 1 ? 's' : ''} ({item.qtyPerPallet}/pallet)
                                              {freePct > 0 && <> — last pallet {freePct}% free ({freeSlots} unit{freeSlots !== 1 ? 's' : ''} free)</>}
                                            </p>
                                          );
                                        })()}

                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                          <table className="w-full text-xs">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                              <tr>
                                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600 uppercase tracking-wide w-6">#</th>
                                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600 uppercase tracking-wide">{cardLbdPlacementType === 'rack' ? 'LBD Rack' : 'LBD Zone'}</th>
                                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600 uppercase tracking-wide">Location</th>
                                                {cardLbdPlacementType === 'zone' && (
                                                  <th className="text-left px-2 py-1.5 font-semibold text-gray-600 uppercase tracking-wide w-20">Pallets</th>
                                                )}
                                                <th className="w-8"></th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {cardLbdPlacementType === 'rack' ? cardLbdPlacements.map((lp, idx) => {
                                                const lbdRackLocations = lp.rackId ? (locationsByRackId[lp.rackId as number] || []) : [];
                                                return (
                                                  <tr key={idx}>
                                                    <td className="px-2 py-1.5 text-gray-500 font-medium">{idx + 1}</td>
                                                    <td className="px-2 py-1.5">
                                                      <PanelSelect
                                                        options={[
                                                          { value: '', label: 'Select LBD rack' },
                                                          ...lbdRacks.map((r: any) => ({ value: String(r.id), label: rackOptionLabel(r) })),
                                                        ]}
                                                        value={String(lp.rackId ?? '')}
                                                        onChange={(v) => {
                                                          const rackId = v ? parseInt(v) : null;
                                                          setCardLbdPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, rackId, locationId: null } : r));
                                                          if (rackId) void ensureLocationsLoaded(rackId);
                                                        }}
                                                        placeholder="Select LBD rack"
                                                        className="w-full lg:max-w-[12rem]"
                                                      />
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                      {lbdRackLocations.length > 0 ? (
                                                        <PanelSelect
                                                          options={[
                                                            { value: '', label: 'Select location' },
                                                            ...lbdRackLocations.filter((loc: any) => loc?.locationId).map((loc: any) => {
                                                              // A slot already picked on another LBD pallet row can't
                                                              // be picked again — same guard the non-LBD rack table
                                                              // uses; the backend has no duplicate check of its own
                                                              // for LBD rack placements, so this dropdown is the only
                                                              // thing stopping two pallets from landing on one slot.
                                                              const pickedOnAnotherRow = cardLbdPlacements.some(
                                                                (other, i) => i !== idx && other.locationId === loc.locationId,
                                                              );
                                                              return {
                                                                value: String(loc.locationId),
                                                                label: `${loc.locationCode}${formatLocationSpaceLabel(loc)}`,
                                                                disabled: loc.locationId !== lp.locationId && (pickedOnAnotherRow || loc.status === 'occupied' || loc.isHeld),
                                                              };
                                                            }),
                                                          ]}
                                                          value={String(lp.locationId ?? '')}
                                                          onChange={(v) => {
                                                            const locationId = v ? parseInt(v) : null;
                                                            setCardLbdPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, locationId } : r));
                                                          }}
                                                          placeholder={!lp.rackId ? 'Select rack first' : 'Select location'}
                                                          className="w-full lg:max-w-[12rem]"
                                                        />
                                                      ) : <span className="text-gray-300">{!lp.rackId ? 'Select rack first' : '—'}</span>}
                                                    </td>
                                                    <td className="px-1 py-1.5 text-center">
                                                      {(() => {
                                                        // Row list locks once every required LBD pallet has a row —
                                                        // matches "+ Add Pallet" hiding at the same threshold below.
                                                        const isLbdFull = lbdRequiredPallets > 0 && cardLbdPlacements.length >= lbdRequiredPallets;
                                                        return !isLbdFull ? (
                                                          <button type="button" title="Remove row" onClick={() => setCardLbdPlacements((prev) => prev.filter((_, i) => i !== idx))} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 text-xs">✕</button>
                                                        ) : null;
                                                      })()}
                                                    </td>
                                                  </tr>
                                                );
                                              }) : cardLbdPlacements.map((lp, idx) => {
                                                const lbdZoneLocations = lp.zoneId ? (zoneLocationsByZoneId[lp.zoneId as number] || []) : [];
                                                return (
                                                  <tr key={idx}>
                                                    <td className="px-2 py-1.5 text-gray-500 font-medium">{idx + 1}</td>
                                                    <td className="px-2 py-1.5">
                                                      <PanelSelect
                                                        options={[
                                                          { value: '', label: 'Select LBD zone' },
                                                          ...lbdZones.map((z: any) => ({ value: String(z.id), label: zoneOptionLabel(z) })),
                                                        ]}
                                                        value={String(lp.zoneId ?? '')}
                                                        onChange={(v) => {
                                                          const zoneId = v ? parseInt(v) : ('' as number | '');
                                                          setCardLbdPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, zoneId, zoneLocationId: null } : r));
                                                          if (zoneId) void ensureZoneLocationsLoaded(zoneId);
                                                        }}
                                                        placeholder="Select LBD zone"
                                                        className="w-full lg:max-w-[12rem]"
                                                      />
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                      {lbdZoneLocations.length > 0 ? (
                                                        <PanelSelect
                                                          options={[
                                                            { value: '', label: 'No specific slot' },
                                                            ...lbdZoneLocations.map((loc: any) => {
                                                              // A slot already picked on another row of this same
                                                              // table can't be picked again — matches the backend's
                                                              // "LBD zone location selections cannot be duplicated"
                                                              // rule, but catches it in the dropdown instead of on Save.
                                                              const pickedOnAnotherRow = cardLbdPlacements.some(
                                                                (other, i) => i !== idx && other.zoneLocationId === loc.locationId,
                                                              );
                                                              return {
                                                                value: String(loc.locationId),
                                                                label: `${loc.locationCode}${formatLocationSpaceLabel(loc)}`,
                                                                disabled: loc.locationId !== lp.zoneLocationId && (pickedOnAnotherRow || loc.status === 'occupied' || loc.isHeld),
                                                              };
                                                            }),
                                                          ]}
                                                          value={String(lp.zoneLocationId ?? '')}
                                                          onChange={(v) => {
                                                            const zoneLocationId = v ? parseInt(v) : null;
                                                            setCardLbdPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, zoneLocationId, pallets: zoneLocationId ? '1' : r.pallets } : r));
                                                          }}
                                                          placeholder="No specific slot"
                                                          className="w-full lg:max-w-[12rem]"
                                                        />
                                                      ) : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                      {lp.zoneLocationId ? (
                                                        <span className="text-xs text-gray-500 font-medium">1</span>
                                                      ) : (
                                                        <input
                                                          type="text"
                                                          inputMode="numeric"
                                                          pattern="[0-9]*"
                                                          value={lp.pallets}
                                                          onChange={(e) => setCardLbdPlacements((prev) => prev.map((r, i) => i === idx ? { ...r, pallets: INPUT_SANITIZERS.positiveIntegerFormat(e.target.value) } : r))}
                                                          className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-xs"
                                                        />
                                                      )}
                                                    </td>
                                                    <td className="px-1 py-1.5 text-center">
                                                      {(() => {
                                                        // Row list locks once every required LBD pallet has been placed —
                                                        // matches "+ Add Pallet" hiding at the same threshold below.
                                                        const placedLbd = cardLbdPlacements.reduce((s, p) => s + (parseInt(p.pallets) || 0), 0);
                                                        const isLbdFull = lbdRequiredPallets > 0 && placedLbd >= lbdRequiredPallets;
                                                        return !isLbdFull ? (
                                                          <button type="button" title="Remove row" onClick={() => setCardLbdPlacements((prev) => prev.filter((_, i) => i !== idx))} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 text-xs">✕</button>
                                                        ) : null;
                                                      })()}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                              {cardLbdPlacements.length === 0 && (
                                                <tr><td colSpan={cardLbdPlacementType === 'rack' ? 4 : 5} className="px-3 py-3 text-center text-gray-400">No LBD pallets added yet.</td></tr>
                                              )}
                                            </tbody>
                                          </table>
                                          <div className="px-2 py-1.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                                            {(() => {
                                              const placedLbd = cardLbdPlacementType === 'rack'
                                                ? cardLbdPlacements.length
                                                : cardLbdPlacements.reduce((s, p) => s + (parseInt(p.pallets) || 0), 0);
                                              const isLbdFull = lbdRequiredPallets > 0 && placedLbd >= lbdRequiredPallets;
                                              return (
                                                <>
                                                  {!isLbdFull && (
                                                    <button
                                                      type="button"
                                                      onClick={() => setCardLbdPlacements((prev) => [
                                                        ...prev,
                                                        cardLbdPlacementType === 'rack'
                                                          ? { zoneId: '', pallets: '1', zoneLocationId: null, rackId: prev.length > 0 ? prev[prev.length - 1].rackId ?? null : null, locationId: null }
                                                          : { zoneId: prev.length > 0 ? prev[prev.length - 1].zoneId : '', pallets: '1', zoneLocationId: null },
                                                      ])}
                                                      className="inline-flex items-center gap-1 px-2.5 py-1 border-2 border-dashed border-red-300 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 hover:border-red-400 transition-colors"
                                                    >
                                                      + Add Pallet
                                                    </button>
                                                  )}
                                                  <span className="text-xs font-semibold text-gray-600">
                                                    {placedLbd} pallet(s)
                                                  </span>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>

                                        <div className="w-full sm:w-64">
                                          <label className="block text-xs font-medium text-gray-700 mb-1">LBD Remark</label>
                                          <input type="text" value={cardLbdRemark} onChange={(e) => setCardLbdRemark(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs" placeholder="Optional" />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : null;

                            const footerActions = (
                              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <button type="button" onClick={resetCardAssignment} className="w-full sm:w-auto px-4 py-2 min-h-[40px] bg-white text-gray-600 text-xs font-semibold rounded-lg border border-gray-300 hover:bg-gray-50">
                                  Cancel
                                </button>
                                <button type="button" onClick={() => handleSavePalletAssignment(item)} disabled={updateItemMutation.isPending} className="w-full sm:w-auto px-5 py-2 min-h-[40px] bg-brand-600 text-white text-xs font-semibold rounded-lg hover:bg-brand-700 disabled:opacity-50 shadow-sm">
                                  {updateItemMutation.isPending ? 'Saving...' : 'Save Assignment'}
                                </button>
                              </div>
                            );

                            // Split layout with a live summary rail
                            const panelContent = (
                                <div className="w-full bg-white rounded-xl border-2 border-brand-300 shadow-xl overflow-hidden">
                                  <div className="flex flex-col lg:flex-row">
                                    {/* Summary rail */}
                                    <div className="lg:w-60 shrink-0 bg-gradient-to-b from-brand-600 to-brand-800 text-white p-4 lg:p-5">
                                      <p className="text-[11px] uppercase tracking-widest text-brand-200 font-bold">Assign Pallets</p>
                                      <p className="text-sm font-bold mt-1 leading-snug break-words">{item.productDescription}</p>
                                      <div className="mt-4 grid grid-cols-2 lg:grid-cols-1 gap-2">
                                        <div className="bg-white/10 rounded-lg p-3">
                                          <p className="text-[10px] text-brand-200 uppercase tracking-wider">Required</p>
                                          <p className="text-2xl font-extrabold leading-tight">{requiredPallets > 0 ? requiredPallets : '—'}</p>
                                        </div>
                                        <div className="bg-white/10 rounded-lg p-3">
                                          <p className="text-[10px] text-brand-200 uppercase tracking-wider">Placed</p>
                                          <p className="text-2xl font-extrabold leading-tight">{placedPallets}</p>
                                          {requiredPallets > 0 && (
                                            <div className="mt-2 h-1.5 bg-white/20 rounded-full overflow-hidden">
                                              <div className={`h-full rounded-full transition-all duration-300 ${placedPallets >= requiredPallets ? 'bg-green-400' : 'bg-amber-300'}`} style={{ width: `${Math.min(100, (placedPallets / requiredPallets) * 100)}%` }} />
                                            </div>
                                          )}
                                        </div>
                                        {(item.cdamQty + item.damQty) > 0 && (
                                          <div className="bg-red-500/25 border border-red-300/40 rounded-lg p-3 col-span-2 lg:col-span-1">
                                            <p className="text-[10px] text-red-100 uppercase tracking-wider">Damaged Units</p>
                                            <p className="text-2xl font-extrabold leading-tight text-red-50">{item.cdamQty + item.damQty}</p>
                                          </div>
                                        )}
                                      </div>
                                      <p className="hidden lg:block mt-4 text-[11px] text-brand-200 leading-relaxed">
                                        {cardPalletMode === 'own' ? `Tip: ${placementHint}` : 'Pick where the shared pallet sits.'}
                                      </p>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 flex flex-col">
                                      <div className="p-4 lg:p-5 space-y-5 flex-1">
                                        {cardPalletMode === 'own' && (
                                          <>
                                            <div className="space-y-3">
                                              <p className="text-[11px] font-bold text-brand-500 uppercase tracking-widest">Pallet Details</p>
                                              {palletDetailsFields}
                                            </div>
                                            <div>
                                              <p className="text-[11px] font-bold text-brand-500 uppercase tracking-widest mb-2">Placement</p>
                                              {placementSection}
                                            </div>
                                          </>
                                        )}
                                        {sharedSection}
                                        {lbdSection}
                                      </div>
                                      {footerActions}
                                    </div>
                                  </div>
                                </div>
                              );

                            return (
                              <>
                                {/* Desktop: inline expandable row under the product */}
                                <tr className="grn-panel-row hidden lg:table-row bg-brand-50 border-t border-brand-100">
                                  <td colSpan={20} className="px-3 py-4 lg:px-6">{panelContent}</td>
                                </tr>
                                {/* Mobile/tablet: bottom sheet — the wide scrolling table would
                                    otherwise force sideways scrolling through the whole form */}
                                {createPortal(
                                  <div className="lg:hidden fixed inset-0 z-50">
                                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={resetCardAssignment}></div>
                                    <div className="fixed inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl animate-slide-up">
                                      {panelContent}
                                    </div>
                                  </div>,
                                  document.body,
                                )}
                              </>
                            );
                          })()}
                  </Fragment>
                );
              })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Confirm GRN Button */}
        {(isDraft || isRejected) && items.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={handleConfirmGrn}
              disabled={confirmGrnMutation.isPending}
              className="w-full lg:w-auto justify-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-lg font-semibold"
            >
              {confirmGrnMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {isRejected ? 'Resubmit GRN for Approval' : 'Submit GRN for Approval'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      <ConfirmDialog />
    </Layout>
  );
}